import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './ProjectsPage.css';
import HomeButton from './HomeButton';
import api from '../services/api';
import ProductsIcon from '../assets/icons/Products.png';
import ProjectProductsServices from './ProjectProductsServices';
import ProjectAttachments from './ProjectAttachments';
import { setBreadcrumb, prettyLabel } from '../services/breadcrumb';
import QuotesIcon from '../assets/icons/Quotes.png';
import InvoicesIcon from '../assets/icons/Invoices.png';
import TasksIcon from '../assets/icons/Tasks.png';
import NotesIcon from '../assets/icons/Notes.png';
import ProjectsIcon from '../assets/icons/Projects.png';
import PurchaseOrderIcon from '../assets/icons/PurchaseOrders.png';
import ProjectTrackerIcon from '../assets/icons/ProjectTracker.png';
import RcsIcon from '../assets/icons/Rcs.png';
import AttachmentsIcon from '../assets/icons/Attachments.png';
import MoodBoardIcon from '../assets/icons/MoodBoard.png';
import InvoiceLedgerEdit from './InvoiceLedgerEdit';
import RoomLayoutEditor from './RoomLayoutEditor';
import RoomLayoutPreview from './RoomLayoutPreview';
import MoodBoardEditor from './MoodBoardEditor';
import ProjectTracker from './ProjectTracker';
import useAutoSave, { autoSaveLabel } from '../hooks/useAutoSave';
import PersistentTabNotes from './PersistentTabNotes';
import RetainerCreditsPanel from './RetainerCreditsPanel';
import { startProjectTimer, stopProjectTimer } from '../services/timeTracker';
import { getCurrentUser, TaskConversationBubble } from './TaskConversation';
import TaskSubtasks from './TaskSubtasks';
import { TaskGroup, TaskListColumns, TaskDetailView, groupTasksBy, useSubtaskCounts } from './TasksPage';
import './TasksPage.css';

// Human labels for the project-detail and room-detail tabs (breadcrumb).
const PROJECT_TAB_LABELS = {
  rooms: 'Rooms', mood_board: 'Mood Board', products_services: 'Products & Services', project_tracker: 'Project Tracker',
  invoices: 'Invoices', quotes: 'Proposals', purchase_orders: 'Purchase Orders', tasks: 'Tasks',
  retainers_credits_srisd: 'Retainers/Credits/SRISD', notes: 'Notes', attachments: 'Attachments',
};
const ROOM_TAB_LABELS = {
  images: 'Images', drawings: 'Drawings', layout: 'Layout', notes: 'Notes',
  AOS_Products: 'Products', AOS_Quotes: 'Proposals', AOS_Invoices: 'Invoices', Tasks: 'Tasks',
};

// The real end user id (the app authenticates as the shared apiuser, so project
// endpoints need the real user passed as user_id to scope "assigned" access).
const currentUserId = () => {
  try { return (JSON.parse(sessionStorage.getItem('user') || 'null') || {}).id || null; }
  catch (e) { return null; }
};

const CHAT_API_BASE = '/api/chat';

// Authenticated fetch wrapper - adds Bearer token to all requests
const authFetch = async (url, options = {}) => {
  const token = await api.ensureAuthenticated();
  const headers = {
    ...(options.headers || {}),
    'Authorization': `Bearer ${token}`,
  };
  return fetch(url, { ...options, headers });
};


// Status and Priority options
const STATUS_OPTIONS = ['Draft', 'In Progress', 'On Hold', 'Completed'];
const PRIORITY_OPTIONS = ['Low', 'Medium', 'High'];



// Room Edit Form Component
const RoomEditForm = ({ room, projectId, onSave, onCancel }) => {
  const attrs = room?.attributes || {};
  const isEdit = !!room?.id;
  const [formData, setFormData] = useState({
    name: attrs.name || '',
    description: attrs.description || '',
    image_path: attrs.image_path || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Auto-save (edit mode only): persist current form via PUT.
  const saveCurrent = async () => {
    const response = await authFetch(`${CHAT_API_BASE}/rooms.php`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: room?.id, project_id: projectId, ...formData })
    });
    const data = await response.json();
    if (!response.ok || !data.success) throw new Error(data.error || 'Failed to save room');
  };
  const { schedule, flush, status: autoStatus } = useAutoSave(saveCurrent);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (isEdit) schedule();
  };

  // Edit mode: flush pending edit then close (parent refetches). Create mode: POST.
  const handleDone = async () => { await flush(); onSave(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEdit) { handleDone(); return; }
    setSaving(true);
    setError(null);

    try {
      const response = await authFetch(`${CHAT_API_BASE}/rooms.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: room?.id,
          project_id: projectId,
          ...formData
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save room');
      }

      const data = await response.json();
      if (data.success) {
        onSave(data.data);
      } else {
        throw new Error(data.error || 'Failed to save room');
      }
    } catch (err) {
      console.error('Failed to save room:', err);
      setError(err.message);
    }
    setSaving(false);
  };

  return (
    <div className="room-edit-page">
      <div className="room-edit-header">
        <button className="back-button" onClick={isEdit ? handleDone : onCancel}>
          ← Back
        </button>
        <h1 className="room-edit-title">
          {room?.id ? 'Edit Room' : 'New Room'}
        </h1>
      </div>

      <form className="room-edit-form" onSubmit={handleSubmit}>
        {error && (
          <div className="form-error">
            <span>⚠️</span> {error}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="room-name">Room Name *</label>
          <input
            type="text"
            id="room-name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Enter room name (e.g., Living Room, Kitchen)"
          />
        </div>

        <div className="form-group">
          <label htmlFor="room-description">Description</label>
          <textarea
            id="room-description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            placeholder="Enter room description"
          />
        </div>

        <div className="form-group">
          <label htmlFor="room-image">Image URL</label>
          <input
            type="text"
            id="room-image"
            name="image_path"
            value={formData.image_path}
            onChange={handleChange}
            placeholder="Enter image URL (optional)"
          />
          {formData.image_path && (
            <div className="image-preview">
              <img src={formData.image_path} alt="Room preview" onError={(e) => e.target.style.display = 'none'} />
            </div>
          )}
        </div>

        <div className="form-actions">
          {isEdit ? (
            <>
              <span className={`form-autosave form-autosave-${autoStatus}`}>{autoSaveLabel(autoStatus)}</span>
              <button type="button" className="btn-save" onClick={handleDone}>Done</button>
            </>
          ) : (
            <>
              <button type="button" className="btn-cancel" onClick={onCancel} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn-save" disabled={saving}>
                {saving ? 'Saving...' : 'Save Room'}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
};


// Room Product Edit Form (stable component - not defined inside render)
const ROOM_PRODUCT_EDIT_FIELDS = [
  { key: 'name', label: 'Product Name', type: 'text' },
  { key: 'identifier', label: 'Identifier', type: 'text' },
  { key: 'client_facing_name_c', label: 'Client Facing Name', type: 'text' },
  { key: 'part_number', label: 'SKU', type: 'text' },
  { key: 'category', label: 'Category', type: 'text' },
  { key: 'description', label: 'Description', type: 'textarea' },
  { key: 'price', label: 'Price', type: 'number' },
  { key: 'cost', label: 'Cost', type: 'number' },
  { key: 'quantity', label: 'Quantity', type: 'number' },
  { key: 'manufacturer_c', label: 'Manufacturer', type: 'text' },
  { key: 'vendor_c', label: 'Vendor', type: 'text' },
  { key: 'color_c', label: 'Color', type: 'text' },
  { key: 'finish_c', label: 'Finish', type: 'text' },
  { key: 'material_c', label: 'Material', type: 'text' },
  { key: 'url', label: 'Product URL', type: 'text' },
  { key: 'shipping_address', label: 'Shipping Address', type: 'textarea' },
  { key: 'notes', label: 'Room Notes', type: 'textarea' },
];

// Inline-editable identifier shown on a Room Product card. Auto-generated
// server-side (abbreviated category + per-category number, e.g. LIG-1) but
// freely editable here; persists on blur.
const RoomProductIdentifier = ({ value, onSave }) => {
  const [val, setVal] = useState(value || '');
  useEffect(() => { setVal(value || ''); }, [value]);
  const commit = () => {
    const v = val.trim();
    if (v !== (value || '')) onSave(v);
  };
  return (
    <input
      className="room-product-identifier-input"
      value={val}
      placeholder="ID"
      title="Identifier (editable)"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onChange={(e) => setVal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
    />
  );
};

const RoomProductEditForm = ({ roomProduct, onSave, onDelete, saving }) => {
  const rpAttrs = roomProduct.attributes || {};
  const [fd, setFd] = useState(() => {
    const init = {};
    ROOM_PRODUCT_EDIT_FIELDS.forEach(f => { init[f.key] = rpAttrs[f.key] || ''; });
    return init;
  });
  const fdRef = useRef(fd);
  fdRef.current = fd;

  // Reset form when a different product is selected
  useEffect(() => {
    const init = {};
    const attrs = roomProduct.attributes || {};
    ROOM_PRODUCT_EDIT_FIELDS.forEach(f => { init[f.key] = attrs[f.key] || ''; });
    setFd(init);
  }, [roomProduct.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save edits directly (without closing). Closing still persists + refreshes via onSave.
  const saveCurrent = async () => {
    const res = await authFetch(`${CHAT_API_BASE}/room-products.php`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: roomProduct.id, ...fdRef.current }),
    });
    const result = await res.json();
    if (!result.success) throw new Error(result.error || 'Failed to save');
  };
  const { schedule, flush, status: autoStatus } = useAutoSave(saveCurrent);

  const updateField = (key, value) => {
    setFd(prev => ({ ...prev, [key]: value }));
    schedule();
  };

  const handleClose = async () => {
    await flush();
    onSave(fdRef.current);
  };

  return (
    <div className="room-product-edit-page">
      <div className="room-product-edit-header">
        <button className="back-button" onClick={handleClose}>
          ← Back to Products
        </button>
        <h2 className="room-product-edit-title">{fd.name || 'Edit Room Product'}</h2>
        <div className="room-product-edit-actions">
          <button
            className="btn-delete-room-product"
            onClick={() => onDelete(roomProduct.id)}
          >Delete</button>
          <span className={`form-autosave form-autosave-${autoStatus}`}>{autoSaveLabel(autoStatus)}</span>
          <button
            className="btn-save-room-product"
            onClick={handleClose}
          >Done</button>
        </div>
      </div>
      <div className="room-product-edit-grid">
        {ROOM_PRODUCT_EDIT_FIELDS.map(f => (
          <div key={f.key} className={`room-product-edit-field ${f.type === 'textarea' ? 'full-width' : ''}`}>
            <label>{f.label}</label>
            {f.type === 'textarea' ? (
              <textarea
                value={fd[f.key]}
                onChange={(e) => updateField(f.key, e.target.value)}
                rows={3}
              />
            ) : (
              <input
                type={f.type}
                value={fd[f.key]}
                onChange={(e) => updateField(f.key, e.target.value)}
                step={f.type === 'number' ? 'any' : undefined}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Room Detail View Component
const RoomDetailView = ({ room, project, onBack, onHome, onNavigateToProjectTab, onCoverChanged }) => {
  const [activeTab, setActiveTab] = useState('room');

  // Breadcrumb: Projects → <project> → Rooms → <room> [→ <room tab>]. The room
  // view is the deepest level, so it always sets the full path.
  useEffect(() => {
    const projName = project?.attributes?.name || 'Project';
    const roomName = room?.attributes?.name || 'Room';
    const crumb = ['Projects', projName, 'Rooms', roomName];
    if (activeTab && activeTab !== 'room') {
      crumb.push(ROOM_TAB_LABELS[activeTab] || prettyLabel(activeTab));
    }
    setBreadcrumb(crumb);
  }, [project, room, activeTab]);

  // Images state
  const [images, setImages] = useState([]);
  const [settingCoverId, setSettingCoverId] = useState(null);
  const [renamingImageId, setRenamingImageId] = useState(null);
  const [renamingImageDraft, setRenamingImageDraft] = useState('');
  const [savingImageRename, setSavingImageRename] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newImageName, setNewImageName] = useState('');
  const [savingImage, setSavingImage] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Drawings state
  const [drawings, setDrawings] = useState([]);
  const [loadingDrawings, setLoadingDrawings] = useState(false);
  const [newDrawingUrl, setNewDrawingUrl] = useState('');
  const [newDrawingName, setNewDrawingName] = useState('');
  const [newDrawingType, setNewDrawingType] = useState('');
  const [savingDrawing, setSavingDrawing] = useState(false);
  const [uploadingDrawing, setUploadingDrawing] = useState(false);
  const [selectedDrawingFile, setSelectedDrawingFile] = useState(null);

  // CRM module records state (for non-Products tabs)
  const [moduleRecords, setModuleRecords] = useState([]);
  const [loadingModuleRecords, setLoadingModuleRecords] = useState(false);

  // Products catalog state (left column)
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productCategories, setProductCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [productImages, setProductImages] = useState({});
  const [catalogHidden, setCatalogHidden] = useState(() => {
    try { return localStorage.getItem('roomProducts.catalogHidden') === '1'; } catch { return false; }
  });
  const toggleCatalogHidden = () => {
    setCatalogHidden(prev => {
      const next = !prev;
      try { localStorage.setItem('roomProducts.catalogHidden', next ? '1' : '0'); } catch {}
      return next;
    });
  };

  // Room products state (right column)
  const [roomProducts, setRoomProducts] = useState([]);
  const [filteredRoomProducts, setFilteredRoomProducts] = useState([]);
  const [loadingRoomProducts, setLoadingRoomProducts] = useState(false);
  const [editingRoomProduct, setEditingRoomProduct] = useState(null);
  const [savingRoomProduct, setSavingRoomProduct] = useState(false);
  const [dragOverRoomProducts, setDragOverRoomProducts] = useState(false);
  const [checkedQuoteItems, setCheckedQuoteItems] = useState({});
  const [taxableQuoteItems, setTaxableQuoteItems] = useState({});
  const [generatingQuote, setGeneratingQuote] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);

  // Room-level invoices state
  const [roomInvoices, setRoomInvoices] = useState([]);
  const [loadingRoomInvoices, setLoadingRoomInvoices] = useState(false);
  const [selectedRoomInvoice, setSelectedRoomInvoice] = useState(null);
  const [roomInvoiceDetail, setRoomInvoiceDetail] = useState(null);
  const [loadingInvoiceDetail, setLoadingInvoiceDetail] = useState(false);

  // Room tasks state
  const [roomTasks, setRoomTasks] = useState([]);
  const [loadingRoomTasks, setLoadingRoomTasks] = useState(false);
  const [editingRoomTask, setEditingRoomTask] = useState(null);
  // Bumped after any patch so the Subtasks progress bar refreshes even
  // though the parent-task id list itself hasn't changed.
  const [roomCountsVersion, setRoomCountsVersion] = useState(0);
  // Populate the Subtasks column in the room-tasks list (shared with the
  // standalone Tasks page via the exported hook).
  const roomSubtaskCounts = useSubtaskCounts(roomTasks, roomCountsVersion);
  const [savingRoomTask, setSavingRoomTask] = useState(false);
  const [roomTaskUsers, setRoomTaskUsers] = useState([]);

  const MODULE_TABS = [
    { id: 'AOS_Products', label: 'Products', icon: ProductsIcon },
    { id: 'Tasks', label: 'Tasks', icon: TasksIcon },
  ];

  const attrs = room.attributes || {};

  const fetchImages = useCallback(async () => {
    setLoadingImages(true);
    try {
      const response = await authFetch(`${CHAT_API_BASE}/room-images.php?room_id=${room.id}`);
      const data = await response.json();
      if (data.success) {
        setImages(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch images:', err);
    }
    setLoadingImages(false);
  }, [room.id]);

  // Choose which uploaded image is shown on the room card. Persisted via
  // is_cover; clears any previously chosen cover for this room.
  const handleSetCoverImage = async (imageId) => {
    setSettingCoverId(imageId);
    // Optimistic: reflect the new cover in the list immediately.
    setImages(prev => prev.map(im => ({
      ...im,
      attributes: { ...im.attributes, is_cover: im.id === imageId ? 1 : 0 },
    })));
    try {
      await authFetch(`${CHAT_API_BASE}/room-images.php?id=${encodeURIComponent(imageId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ set_cover: true }),
      });
      if (onCoverChanged) onCoverChanged(room.id); // refresh the room card in the list
    } catch (err) {
      console.error('Failed to set cover image:', err);
      fetchImages(); // revert to server truth on failure
    }
    setSettingCoverId(null);
  };

  const fetchDrawings = useCallback(async () => {
    setLoadingDrawings(true);
    try {
      const response = await authFetch(`${CHAT_API_BASE}/room-drawings.php?room_id=${room.id}`);
      const data = await response.json();
      if (data.success) {
        setDrawings(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch drawings:', err);
    }
    setLoadingDrawings(false);
  }, [room.id]);

  const fetchModuleRecords = useCallback(async (moduleName) => {
    setLoadingModuleRecords(true);
    try {
      const res = await api.getRecords(moduleName, { pageSize: 20, sort: '-date_modified' });
      const records = res?.data?.data || res?.data || [];
      setModuleRecords(Array.isArray(records) ? records : []);
    } catch (err) {
      console.error(`Failed to fetch ${moduleName} records:`, err);
      setModuleRecords([]);
    }
    setLoadingModuleRecords(false);
  }, []);

  const fetchAllProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const response = await fetch(`${CHAT_API_BASE}/products.php`);
      const data = await response.json();
      const productList = data.data || [];
      setAllProducts(productList);

      // Extract unique categories
      const cats = [...new Set(
        productList.map(p => p.attributes?.category).filter(c => c && c.trim())
      )].sort();
      setProductCategories(cats);

      // Fetch images for each product
      const imagesMap = {};
      await Promise.all(productList.map(async (product) => {
        try {
          const imgRes = await fetch(`${CHAT_API_BASE}/product-images.php?product_id=${product.id}`);
          if (imgRes.ok) {
            const imgData = await imgRes.json();
            if (imgData.success && imgData.images) {
              imagesMap[product.id] = imgData.images;
            }
          }
        } catch (e) { /* skip */ }
      }));
      setProductImages(imagesMap);
    } catch (err) {
      console.error('Failed to fetch products:', err);
      setAllProducts([]);
    }
    setLoadingProducts(false);
  }, []);

  const fetchRoomProducts = useCallback(async () => {
    setLoadingRoomProducts(true);
    try {
      const response = await authFetch(`${CHAT_API_BASE}/room-products.php?room_id=${room.id}`);
      const data = await response.json();
      if (data.success) {
        const rps = data.data || [];
        setRoomProducts(rps);
        // Initialize from persisted `included` and `taxable` fields
        const checked = {};
        const taxable = {};
        rps.forEach(rp => {
          checked[rp.id] = rp.attributes?.included !== '0' && rp.attributes?.included !== 0;
          // Missing/null defaults to taxable (back-compat for rows pre-migration)
          const t = rp.attributes?.taxable;
          taxable[rp.id] = t === undefined || t === null || (t !== '0' && t !== 0);
        });
        setCheckedQuoteItems(checked);
        setTaxableQuoteItems(taxable);
      }
    } catch (err) {
      console.error('Failed to fetch room products:', err);
    }
    setLoadingRoomProducts(false);
  }, [room.id]);

  const fetchRoomInvoices = useCallback(async () => {
    if (!room?.id) return;
    setLoadingRoomInvoices(true);
    try {
      const response = await authFetch(`${CHAT_API_BASE}/project-invoices.php?room_id=${room.id}`);
      const data = await response.json();
      if (data.success) {
        setRoomInvoices(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch room invoices:', err);
    }
    setLoadingRoomInvoices(false);
  }, [room?.id]);

  const fetchInvoiceDetail = useCallback(async (invoiceId) => {
    setLoadingInvoiceDetail(true);
    try {
      const response = await authFetch(`${CHAT_API_BASE}/project-invoices.php?id=${invoiceId}`);
      const data = await response.json();
      if (data.success) {
        setRoomInvoiceDetail(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch invoice detail:', err);
    }
    setLoadingInvoiceDetail(false);
  }, []);

  // Build line items from checked room products
  const getCheckedLineItems = () => {
    return roomProducts
      .filter(rp => checkedQuoteItems[rp.id])
      .map(rp => {
        const a = rp.attributes || {};
        return {
          name: a.client_facing_name_c || a.name || 'Item',
          part_number: a.part_number || '',
          description: a.description || '',
          quantity: parseInt(a.quantity, 10) || 1,
          unit_price: parseFloat(a.price) || 0,
          product_id: a.source_product_id || null,
          room_product_id: rp.id,
          taxable: taxableQuoteItems[rp.id] !== false,
        };
      });
  };

  const handleGenerateQuote = async () => {
    const lineItems = getCheckedLineItems();
    if (lineItems.length === 0) { alert('No items selected.'); return; }
    setGeneratingQuote(true);
    try {
      const projectId = project?.id || room.attributes?.project_id;
      const projectName = project?.attributes?.name || 'Project';
      const roomName = room.attributes?.name || 'Room';
      const response = await authFetch(`${CHAT_API_BASE}/project-quotes.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${projectName} - ${roomName} Proposal`,
          project_id: projectId,
          room_id: room?.id || null,
          stage: 'Draft',
          expiration: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
          line_items: lineItems,
        }),
      });
      const data = await response.json();
      if (data.success) {
        alert(`Proposal #${data.data?.attributes?.number || ''} created. Opening Proposals tab.`);
        if (onNavigateToProjectTab) onNavigateToProjectTab('quotes');
      } else {
        alert('Failed to generate quote: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Generate quote error:', err);
      alert('Failed to generate quote: ' + err.message);
    }
    setGeneratingQuote(false);
  };

  const handleCreateInvoice = async () => {
    const lineItems = getCheckedLineItems();
    if (lineItems.length === 0) { alert('No items selected.'); return; }
    setCreatingInvoice(true);
    try {
      const projectId = project?.id || room.attributes?.project_id;
      const projectName = project?.attributes?.name || 'Project';
      const roomName = room.attributes?.name || 'Room';
      const today = new Date().toISOString().split('T')[0];
      const due = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];
      const response = await authFetch(`${CHAT_API_BASE}/project-invoices.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${projectName} - ${roomName} Invoice`,
          project_id: projectId,
          room_id: room?.id || null,
          status: 'Draft',
          invoice_date: today,
          due_date: due,
          line_items: lineItems,
          tax_amount: 0,
          shipping_amount: 0,
        }),
      });
      const data = await response.json();
      if (data.success) {
        alert(`Invoice #${data.data?.attributes?.number || ''} created. Opening Invoices tab.`);
        if (onNavigateToProjectTab) onNavigateToProjectTab('invoices');
      } else {
        alert('Failed to create invoice: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Create invoice error:', err);
      alert('Failed to create invoice.');
    }
    setCreatingInvoice(false);
  };

  // Search filter helper
  const matchesSearch = (a, q) => {
    return (a.name || '').toLowerCase().includes(q)
      || (a.part_number || '').toLowerCase().includes(q)
      || (a.description || '').toLowerCase().includes(q)
      || (a.manufacturer_c || '').toLowerCase().includes(q)
      || (a.client_facing_name_c || '').toLowerCase().includes(q);
  };

  // Filter catalog products by search + category
  useEffect(() => {
    let filtered = allProducts;
    if (selectedCategory) {
      filtered = filtered.filter(p => p.attributes?.category === selectedCategory);
    }
    if (productSearch.trim()) {
      const q = productSearch.toLowerCase();
      filtered = filtered.filter(p => matchesSearch(p.attributes || {}, q));
    }
    setFilteredProducts(filtered);
  }, [allProducts, productSearch, selectedCategory]);

  // Filter room products by same search
  useEffect(() => {
    if (productSearch.trim()) {
      const q = productSearch.toLowerCase();
      setFilteredRoomProducts(roomProducts.filter(rp => matchesSearch(rp.attributes || {}, q)));
    } else {
      setFilteredRoomProducts(roomProducts);
    }
  }, [roomProducts, productSearch]);

  // Drag-and-drop handlers
  const handleDragStart = (e, product) => {
    e.dataTransfer.setData('text/plain', product.id);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOverRoomProducts(true);
  };

  const handleDragLeave = (e) => {
    // Only clear if leaving the drop zone (not entering a child)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverRoomProducts(false);
    }
  };

  const handleDropProduct = async (e) => {
    e.preventDefault();
    setDragOverRoomProducts(false);
    const productId = e.dataTransfer.getData('text/plain');
    const product = allProducts.find(p => p.id === productId);
    if (!product) return;

    const a = product.attributes || {};
    try {
      await authFetch(`${CHAT_API_BASE}/room-products.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          room_id: room.id,
          source_product_id: product.id,
          name: a.name || '',
          part_number: a.part_number || '',
          description: a.description || '',
          price: a.price || null,
          cost: a.cost || null,
          url: a.url || '',
          category: a.category || '',
          manufacturer_c: a.manufacturer_c || '',
          client_facing_name_c: a.client_facing_name_c || '',
          color_c: a.color_c || '',
          finish_c: a.finish_c || '',
          material_c: a.material_c || '',
          vendor_c: a.vendor_c || '',
        }),
      });
      fetchRoomProducts();
    } catch (err) {
      console.error('Failed to add product to room:', err);
    }
  };

  // Also support click-to-add for mobile/touch
  const handleAddProductToRoom = async (product) => {
    const a = product.attributes || {};
    try {
      await authFetch(`${CHAT_API_BASE}/room-products.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          room_id: room.id,
          source_product_id: product.id,
          name: a.name || '',
          part_number: a.part_number || '',
          description: a.description || '',
          price: a.price || null,
          cost: a.cost || null,
          url: a.url || '',
          category: a.category || '',
          manufacturer_c: a.manufacturer_c || '',
          client_facing_name_c: a.client_facing_name_c || '',
          color_c: a.color_c || '',
          finish_c: a.finish_c || '',
          material_c: a.material_c || '',
          vendor_c: a.vendor_c || '',
        }),
      });
      fetchRoomProducts();
    } catch (err) {
      console.error('Failed to add product to room:', err);
    }
  };

  const handleDeleteRoomProduct = async (id) => {
    try {
      await authFetch(`${CHAT_API_BASE}/room-products.php?id=${id}`, { method: 'DELETE' });
      fetchRoomProducts();
    } catch (err) {
      console.error('Failed to delete room product:', err);
    }
  };

  const handleSaveRoomProductIdentifier = async (id, identifier) => {
    try {
      await authFetch(`${CHAT_API_BASE}/room-products.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, identifier }),
      });
      setRoomProducts(prev => prev.map(p =>
        p.id === id ? { ...p, attributes: { ...p.attributes, identifier } } : p
      ));
    } catch (err) {
      console.error('Failed to save room product identifier:', err);
    }
  };

  const handleSaveRoomProduct = async (formData) => {
    const rpId = editingRoomProduct?.id;
    if (!rpId) return;
    setSavingRoomProduct(true);
    try {
      const payload = { id: rpId, ...formData };
      const res = await authFetch(`${CHAT_API_BASE}/room-products.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (!result.success) {
        console.error('Save room product failed:', result);
      }
      setEditingRoomProduct(null);
      fetchRoomProducts();
    } catch (err) {
      console.error('Failed to save room product:', err);
    }
    setSavingRoomProduct(false);
  };


  useEffect(() => {
    if (activeTab === 'room') {
      fetchRoomProducts();
      fetchAllProducts(); // for product thumbnails in the overview
    } else if (activeTab === 'images') {
      fetchImages();
    } else if (activeTab === 'drawings') {
      fetchDrawings();
    } else if (activeTab === 'AOS_Products') {
      fetchAllProducts();
      fetchRoomProducts();
    } else if (activeTab === 'AOS_Quotes') {
      fetchRoomProducts();
      fetchAllProducts(); // for product images
    } else if (activeTab === 'AOS_Invoices') {
      fetchRoomInvoices();
    } else if (activeTab === 'Tasks') {
      fetchRoomTasks();
    } else if (MODULE_TABS.some(t => t.id === activeTab)) {
      fetchModuleRecords(activeTab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fetchImages, fetchDrawings, fetchModuleRecords, fetchAllProducts, fetchRoomProducts, fetchRoomInvoices]);

  const fetchRoomTasks = useCallback(async () => {
    const projectId = project?.id || room.attributes?.project_id;
    if (!projectId) return;
    setLoadingRoomTasks(true);
    try {
      // Pass the real user id so task visibility is scoped to them (not the
      // shared apiuser, which would hide tasks they created).
      const uid = getCurrentUser()?.id ? `&user_id=${encodeURIComponent(getCurrentUser().id)}` : '';
      const response = await authFetch(`${CHAT_API_BASE}/project-tasks.php?project_id=${projectId}&room_id=${room.id}${uid}`);
      const data = await response.json();
      if (data.success) setRoomTasks(data.data || []);
    } catch (err) {
      console.error('Failed to fetch room tasks:', err);
    }
    setLoadingRoomTasks(false);
  }, [project, room]);

  // Fetch users for task assignment dropdown
  useEffect(() => {
    (async () => {
      try {
        const data = await api.chatRequest('GET', `${CHAT_API_BASE}/users.php`);
        if (data.success && data.users) setRoomTaskUsers(data.users);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const handleSaveRoomTask = async (taskData) => {
    setSavingRoomTask(true);
    try {
      const isNew = !editingRoomTask?.id;
      if (isNew) {
        if (!taskData.name || !taskData.name.trim()) {
          alert('Task subject is required.');
          setSavingRoomTask(false);
          return;
        }
        const projectId = project?.id || room.attributes?.project_id;
        await authFetch(`${CHAT_API_BASE}/project-tasks.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ project_id: projectId, room_id: room.id, created_by: getCurrentUser()?.id, ...taskData }),
        });
      } else {
        await authFetch(`${CHAT_API_BASE}/project-tasks.php`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: editingRoomTask.id, ...taskData }),
        });
      }
      setEditingRoomTask(null);
      fetchRoomTasks();
    } catch (err) {
      console.error('Failed to save room task:', err);
    }
    setSavingRoomTask(false);
  };

  const handleNewRoomTask = () => {
    setEditingRoomTask({ attributes: {} });
  };

  // Image handlers
  const handleAddImage = async () => {
    if (!newImageUrl.trim()) return;

    setSavingImage(true);
    try {
      const response = await authFetch(`${CHAT_API_BASE}/room-images.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          room_id: room.id,
          image_url: newImageUrl,
          name: newImageName
        })
      });

      const data = await response.json();
      if (data.success) {
        setNewImageUrl('');
        setNewImageName('');
        fetchImages();
      }
    } catch (err) {
      console.error('Failed to add image:', err);
    }
    setSavingImage(false);
  };

  const startRenameImage = (image) => {
    setRenamingImageId(image.id);
    setRenamingImageDraft(image.attributes?.name || '');
  };

  const cancelRenameImage = () => {
    setRenamingImageId(null);
    setRenamingImageDraft('');
  };

  const handleSaveImageName = async (imageId) => {
    const newName = renamingImageDraft.trim();
    if (!newName) return;
    setSavingImageRename(true);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/room-images.php?id=${imageId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName }),
      });
      const data = await res.json();
      if (data.success) {
        setImages(prev => prev.map(im =>
          im.id === imageId
            ? { ...im, attributes: { ...(im.attributes || {}), name: newName } }
            : im
        ));
        setRenamingImageId(null);
        setRenamingImageDraft('');
      } else {
        alert(data.message || 'Failed to rename image');
      }
    } catch (e) {
      console.error('Failed to rename image:', e);
      alert('Failed to rename image');
    }
    setSavingImageRename(false);
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Delete this image?')) return;

    try {
      await authFetch(`${CHAT_API_BASE}/room-images.php?id=${imageId}`, {
        method: 'DELETE'
      });
      fetchImages();
    } catch (err) {
      console.error('Failed to delete image:', err);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setNewImageName(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('room_id', room.id);
      formData.append('name', newImageName || selectedFile.name);

      const response = await authFetch(`${CHAT_API_BASE}/room-image-upload.php`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setSelectedFile(null);
        setNewImageName('');
        // Reset file input
        const fileInput = document.getElementById('image-file-input');
        if (fileInput) fileInput.value = '';
        fetchImages();
      } else {
        alert(data.error || 'Failed to upload image');
      }
    } catch (err) {
      console.error('Failed to upload image:', err);
      alert('Failed to upload image');
    }
    setUploadingImage(false);
  };

  // Drawing handlers
  const handleAddDrawing = async () => {
    if (!newDrawingUrl.trim()) return;

    setSavingDrawing(true);
    try {
      const response = await authFetch(`${CHAT_API_BASE}/room-drawings.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          room_id: room.id,
          drawing_url: newDrawingUrl,
          name: newDrawingName,
          drawing_type: newDrawingType
        })
      });

      const data = await response.json();
      if (data.success) {
        setNewDrawingUrl('');
        setNewDrawingName('');
        setNewDrawingType('');
        fetchDrawings();
      }
    } catch (err) {
      console.error('Failed to add drawing:', err);
    }
    setSavingDrawing(false);
  };

  const handleDeleteDrawing = async (drawingId) => {
    if (!window.confirm('Delete this drawing?')) return;

    try {
      await authFetch(`${CHAT_API_BASE}/room-drawings.php?id=${drawingId}`, {
        method: 'DELETE'
      });
      fetchDrawings();
    } catch (err) {
      console.error('Failed to delete drawing:', err);
    }
  };

  const handleDrawingFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedDrawingFile(file);
      setNewDrawingName(file.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleDrawingFileUpload = async () => {
    if (!selectedDrawingFile) return;

    setUploadingDrawing(true);
    try {
      const formData = new FormData();
      formData.append('drawing', selectedDrawingFile);
      formData.append('room_id', room.id);
      formData.append('name', newDrawingName || selectedDrawingFile.name);
      formData.append('drawing_type', newDrawingType);

      const response = await authFetch(`${CHAT_API_BASE}/room-drawing-upload.php`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setSelectedDrawingFile(null);
        setNewDrawingName('');
        setNewDrawingType('');
        // Reset file input
        const fileInput = document.getElementById('drawing-file-input');
        if (fileInput) fileInput.value = '';
        fetchDrawings();
      } else {
        alert(data.error || 'Failed to upload drawing');
      }
    } catch (err) {
      console.error('Failed to upload drawing:', err);
      alert('Failed to upload drawing');
    }
    setUploadingDrawing(false);
  };

  const tabs = [
    { id: 'room', label: attrs.name || 'Room', icon: ProjectsIcon },
    { id: 'images', label: 'Images', icon: '🖼️' },
    { id: 'drawings', label: 'Drawings', icon: '📐' },
    { id: 'layout', label: 'Layout', icon: '🧩' },
    ...MODULE_TABS,
    { id: 'notes', label: 'Notes', icon: NotesIcon }
  ];

  // Editing an existing room-level task takes over the whole page with
  // the same split-view TaskDetailView the standalone Tasks page uses,
  // so all three surfaces (standalone / project / room) share one
  // formatting. New-task creation still renders inline in the Tasks tab
  // below.
  if (editingRoomTask?.id) {
    const flatTasks = roomTasks.map(t => ({ id: t.id, ...(t.attributes || {}) }));
    const flatTask = flatTasks.find(t => t.id === editingRoomTask.id)
      || { id: editingRoomTask.id, ...(editingRoomTask.attributes || {}) };
    const cu = getCurrentUser();
    const patchRoomTask = async (id, changes) => {
      // Optimistically update the currently-editing task so the right
      // column selects don't snap back — matters for subtasks, which
      // aren't in roomTasks and so wouldn't be picked up by fetchRoomTasks.
      setEditingRoomTask(prev => (prev?.id === id)
        ? { ...prev, attributes: { ...(prev.attributes || {}), ...changes } }
        : prev);
      try {
        await authFetch(`${CHAT_API_BASE}/project-tasks.php`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id, ...changes, user_id: cu?.id }),
        });
      } catch { /* keep optimistic */ }
      fetchRoomTasks();
      setRoomCountsVersion(v => v + 1);
    };
    const projInfo = { id: project?.id, name: project?.attributes?.name || 'Project' };
    const projectsByTaskId = new Proxy({}, { get: () => projInfo });
    return (
      <TaskDetailView
        task={flatTask}
        allTasks={flatTasks}
        projectsByTaskId={projectsByTaskId}
        onBack={() => { setEditingRoomTask(null); fetchRoomTasks(); }}
        onSelect={(id, taskObj) => {
          // Subtasks aren't in roomTasks (that list is top-level tasks only),
          // so fall back to the passed subtask object when the id misses.
          const next = roomTasks.find(t => t.id === id) || taskObj;
          if (next) setEditingRoomTask(next);
        }}
        onPatch={patchRoomTask}
        onRefresh={fetchRoomTasks}
        currentUser={cu}
        backLabel="← Back to Tasks"
      />
    );
  }

  return (
    <div className="room-detail-page">
      <div className="room-detail-header">
        <HomeButton onClick={onHome} />
        <button className="back-button" onClick={onBack}>
          ← Back to Project
        </button>
        <div className="room-detail-title-section">
          <span className="room-detail-icon">🏠</span>
          <h1 className="room-detail-title">{attrs.name || 'Unnamed Room'}</h1>
        </div>
      </div>

      <div className="room-detail-content">
        <div className="room-detail-sidebar">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`room-detail-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{typeof tab.icon === 'string' && (tab.icon.endsWith('.svg') || tab.icon.startsWith('/') || tab.icon.startsWith('data:')) ? <img src={tab.icon} alt="" className="tab-icon-svg" /> : tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="room-detail-main">
          {activeTab === 'room' && (() => {
            const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(v) || 0);
            return (
              <div className="room-info-section room-overview">
                {attrs.description && (
                  <p className="room-description">{attrs.description}</p>
                )}
                <div className="room-overview-grid">
                  {/* LEFT: floor-plan layout + totals beneath it */}
                  <div className="room-overview-left">
                    <div className="room-overview-layout">
                      <RoomLayoutPreview
                        roomId={room.id}
                        onOpenLayout={() => setActiveTab('layout')}
                      />
                    </div>
                    <div className="room-overview-totals">
                      <div className="room-overview-total">
                        <span className="room-overview-total-label">Total Items</span>
                        <span className="room-overview-total-value">{fmt(attrs.total_items_value)}</span>
                      </div>
                      <div className="room-overview-total">
                        <span className="room-overview-total-label">Total Markup</span>
                        <span className="room-overview-total-value">{fmt(attrs.total_markup)}</span>
                      </div>
                    </div>
                  </div>

                  {/* RIGHT: attached products */}
                  <div className="room-overview-right">
                    <div className="room-products-col-header">
                      <h3 className="module-section-title">Products</h3>
                      <span className="room-products-count">{roomProducts.length}</span>
                    </div>
                    {loadingRoomProducts ? (
                      <div className="notes-loading">Loading...</div>
                    ) : roomProducts.length === 0 ? (
                      <div className="room-products-drop-zone">
                        <span className="empty-icon">📦</span>
                        <p>No products attached yet.</p>
                      </div>
                    ) : (
                      <div className="room-overview-product-list">
                        {roomProducts.map((rp) => {
                          const a = rp.attributes || {};
                          const sourceImgs = productImages[a.source_product_id] || [];
                          const qty = parseInt(a.quantity, 10) || 1;
                          return (
                            <div
                              key={rp.id}
                              className="room-overview-product"
                              onClick={() => setActiveTab('AOS_Products')}
                            >
                              <div className="room-overview-product-thumb">
                                {sourceImgs.length > 0 && sourceImgs[0].image_path ? (
                                  <img src={'/' + sourceImgs[0].image_path} alt="" />
                                ) : (
                                  <span className="room-overview-product-thumb-placeholder">📦</span>
                                )}
                              </div>
                              <div className="room-overview-product-info">
                                <span className="room-overview-product-name">{a.name || 'Unnamed product'}</span>
                                <span className="room-overview-product-meta">
                                  {a.identifier ? a.identifier + ' · ' : ''}Qty {qty}
                                </span>
                              </div>
                              <span className="room-overview-product-price">{fmt(a.price)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {activeTab === 'notes' && (
            <div className="room-notes-section">
              <div className="tab-notes-container">
                <PersistentTabNotes
                  parentType="room"
                  parentId={room.id}
                  defaultTitle="Notes"
                  mentionContext={{
                    projectId: project?.id || room.attributes?.project_id,
                    projectName: project?.attributes?.name || '',
                    roomId: room.id,
                    roomName: room.attributes?.name || 'Room',
                  }}
                />
              </div>
            </div>
          )}

          {activeTab === 'images' && (
            <div className="room-images-section">
              <div className="media-add-form">
                <h4 className="media-form-title">Upload Image</h4>
                <div className="media-form-row">
                  <input
                    type="file"
                    id="image-file-input"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleFileSelect}
                    className="media-file-input"
                  />
                  <input
                    type="text"
                    value={newImageName}
                    onChange={(e) => setNewImageName(e.target.value)}
                    placeholder="Image name (optional)"
                    className="media-input"
                  />
                  <button
                    className="btn-add-media"
                    onClick={handleFileUpload}
                    disabled={uploadingImage || !selectedFile}
                  >
                    {uploadingImage ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
                {selectedFile && (
                  <div className="media-preview">
                    <img src={URL.createObjectURL(selectedFile)} alt="Preview" />
                  </div>
                )}

                <div className="media-form-divider">
                  <span>or add by URL</span>
                </div>

                <div className="media-form-row">
                  <input
                    type="text"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    placeholder="Image URL"
                    className="media-input media-url-input"
                  />
                  <button
                    className="btn-add-media"
                    onClick={handleAddImage}
                    disabled={savingImage || !newImageUrl.trim()}
                  >
                    {savingImage ? 'Adding...' : 'Add URL'}
                  </button>
                </div>
                {newImageUrl && (
                  <div className="media-preview">
                    <img src={newImageUrl} alt="Preview" onError={(e) => e.target.style.display = 'none'} />
                  </div>
                )}
              </div>

              {loadingImages ? (
                <div className="media-loading">Loading images...</div>
              ) : images.length === 0 ? (
                <div className="media-empty">
                  <span className="empty-icon">🖼️</span>
                  <p>No images yet. Add your first image above.</p>
                </div>
              ) : (
                <div className="media-grid">
                  {images.map(image => {
                    const isCover = Number(image.attributes?.is_cover) === 1;
                    return (
                    <div key={image.id} className={`media-card${isCover ? ' is-cover' : ''}`}>
                      <div className="media-card-image">
                        {isCover && <span className="media-cover-badge">★ Card image</span>}
                        <img
                          src={image.attributes?.image_url}
                          alt={image.attributes?.name || 'Room image'}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '';
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                      <div className="media-card-body">
                        {renamingImageId === image.id ? (
                          <div className="media-rename-row">
                            <input
                              type="text"
                              className="media-rename-input"
                              value={renamingImageDraft}
                              autoFocus
                              onChange={e => setRenamingImageDraft(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') handleSaveImageName(image.id);
                                else if (e.key === 'Escape') cancelRenameImage();
                              }}
                              disabled={savingImageRename}
                            />
                            <button
                              className="btn-media-rename-save"
                              onClick={() => handleSaveImageName(image.id)}
                              disabled={savingImageRename || !renamingImageDraft.trim()}
                            >
                              {savingImageRename ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              className="btn-media-rename-cancel"
                              onClick={cancelRenameImage}
                              disabled={savingImageRename}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <h4
                            className="media-name media-name-clickable"
                            title="Click to rename"
                            onClick={() => startRenameImage(image)}
                          >
                            {image.attributes?.name || 'Untitled'}
                          </h4>
                        )}
                        <span className="media-date">
                          {image.attributes?.date_entered
                            ? new Date(image.attributes.date_entered).toLocaleDateString()
                            : ''}
                        </span>
                        <div className="media-card-actions">
                          {isCover ? (
                            <span className="btn-media-cover is-cover" title="Shown on the room card">★ Card image</span>
                          ) : (
                            <button
                              className="btn-media-cover"
                              onClick={() => handleSetCoverImage(image.id)}
                              disabled={settingCoverId === image.id}
                              title="Show this image on the room card"
                            >
                              {settingCoverId === image.id ? 'Setting…' : 'Use for card'}
                            </button>
                          )}
                          {renamingImageId !== image.id && (
                            <button
                              className="btn-media-rename"
                              onClick={() => startRenameImage(image)}
                            >
                              Rename
                            </button>
                          )}
                          <button
                            className="btn-media-delete"
                            onClick={() => handleDeleteImage(image.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'AOS_Products' && !editingRoomProduct && (
            <div className="room-module-section room-products-section">
              <div className="room-products-toolbar">
                <input
                  type="text"
                  className="room-products-search"
                  placeholder="Search both lists..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                />
                {productCategories.length > 0 && (
                  <select
                    className="room-products-category"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="">All Categories</option>
                    {productCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  className="room-products-toggle-catalog"
                  onClick={toggleCatalogHidden}
                  title={catalogHidden ? 'Show Product Catalog column' : 'Hide Product Catalog column'}
                >
                  {catalogHidden ? '▸ Show Catalog' : '◂ Hide Catalog'}
                </button>
              </div>

              <div className={`room-products-two-col ${catalogHidden ? 'catalog-hidden' : ''}`}>
                {/* LEFT COLUMN: Product Catalog */}
                {!catalogHidden && (
                <div className="room-products-catalog">
                  <div className="room-products-col-header">
                    <h3 className="module-section-title">Product Catalog</h3>
                    <span className="room-products-count">{filteredProducts.length}</span>
                  </div>
                  {loadingProducts ? (
                    <div className="notes-loading">Loading products...</div>
                  ) : filteredProducts.length === 0 ? (
                    <div className="notes-empty">
                      <span className="empty-icon">📦</span>
                      <p>{productSearch || selectedCategory ? 'No matches.' : 'No products.'}</p>
                    </div>
                  ) : (
                    <div className="room-module-list">
                      {filteredProducts.map((product) => {
                        const a = product.attributes || {};
                        const imgs = productImages[product.id] || [];
                        const inRoom = roomProducts.some(rp => rp.attributes?.source_product_id === product.id);
                        const formatCurrency = (v) => v ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v) : '';
                        return (
                          <div
                            key={product.id}
                            className={`room-module-item room-product-card catalog-card ${inRoom ? 'in-room' : ''}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, product)}
                          >
                            <div className="room-product-header">
                              {imgs.length > 0 && imgs[0].image_path && (
                                <div className="room-product-thumb">
                                  <img src={'/' + imgs[0].image_path} alt="" />
                                </div>
                              )}
                              <div className="room-product-summary">
                                <div className="room-module-item-name">{a.name || 'Untitled'}</div>
                                <div className="room-module-item-meta">
                                  {a.part_number && <span className="room-module-item-status">{a.part_number}</span>}
                                  {a.category && <span className="room-module-item-status">{a.category}</span>}
                                  {a.price && <span className="room-product-price">{formatCurrency(a.price)}</span>}
                                </div>
                              </div>
                              <button
                                className="btn-add-to-room"
                                title="Add to room"
                                onClick={(e) => { e.stopPropagation(); handleAddProductToRoom(product); }}
                              >+</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                )}

                {/* RIGHT COLUMN: Room Products */}
                <div
                  className={`room-products-assigned ${dragOverRoomProducts ? 'drag-over' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDropProduct}
                >
                  <div className="room-products-col-header">
                    <h3 className="module-section-title">Room Products</h3>
                    <span className="room-products-count">{filteredRoomProducts.length}</span>
                  </div>
                  {loadingRoomProducts ? (
                    <div className="notes-loading">Loading...</div>
                  ) : filteredRoomProducts.length === 0 ? (
                    <div className="room-products-drop-zone">
                      <span className="empty-icon">📦</span>
                      <p>{productSearch ? 'No matches.' : 'Drag products here or click +'}</p>
                    </div>
                  ) : (
                    <div className="room-module-list">
                      {filteredRoomProducts.map((rp) => {
                        const a = rp.attributes || {};
                        const formatCurrency = (v) => v ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v) : '';
                        const sourceImgs = productImages[a.source_product_id] || [];
                        return (
                          <div
                            key={rp.id}
                            className="room-module-item room-product-card assigned-card"
                            onClick={() => setEditingRoomProduct(rp)}
                          >
                            <div className="room-product-header">
                              {sourceImgs.length > 0 && sourceImgs[0].image_path && (
                                <div className="room-product-thumb">
                                  <img src={'/' + sourceImgs[0].image_path} alt="" />
                                </div>
                              )}
                              <div className="room-product-summary">
                                <div className="room-product-identifier-row">
                                  <RoomProductIdentifier
                                    value={a.identifier}
                                    onSave={(v) => handleSaveRoomProductIdentifier(rp.id, v)}
                                  />
                                </div>
                                <div className="room-module-item-name">{a.name || 'Untitled'}</div>
                                <div className="room-module-item-meta">
                                  {a.part_number && <span className="room-module-item-status">{a.part_number}</span>}
                                  {a.quantity > 1 && <span className="room-module-item-status">Qty: {a.quantity}</span>}
                                  {a.price && <span className="room-product-price">{formatCurrency(a.price)}</span>}
                                </div>
                              </div>
                              <button
                                className="btn-remove-from-room"
                                title="Remove from room"
                                onClick={(e) => { e.stopPropagation(); handleDeleteRoomProduct(rp.id); }}
                              >×</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Room Product Edit Form */}
          {activeTab === 'AOS_Products' && editingRoomProduct && (
            <RoomProductEditForm
              roomProduct={editingRoomProduct}
              onSave={handleSaveRoomProduct}
              onDelete={(id) => { handleDeleteRoomProduct(id); setEditingRoomProduct(null); }}
              saving={savingRoomProduct}
            />
          )}

          {activeTab === 'AOS_Quotes' && (
            <div className="room-module-section room-quotes-section">
              <h3 className="module-section-title">Room Products Proposal</h3>
              {loadingRoomProducts ? (
                <div className="notes-loading">Loading room products...</div>
              ) : roomProducts.length === 0 ? (
                <div className="notes-empty">
                  <span className="empty-icon">💵</span>
                  <p>No room products yet. Add products in the Products tab first.</p>
                </div>
              ) : (() => {
                const formatCurrency = (v) => {
                  const n = parseFloat(v);
                  return isNaN(n) ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
                };
                const allChecked = roomProducts.every(rp => checkedQuoteItems[rp.id]);
                const allTaxable = roomProducts.every(rp => taxableQuoteItems[rp.id] !== false);
                let grandTotal = 0;
                roomProducts.forEach(rp => {
                  if (checkedQuoteItems[rp.id]) {
                    const a = rp.attributes || {};
                    grandTotal += (parseFloat(a.price) || 0) * (parseInt(a.quantity, 10) || 1);
                  }
                });
                return (
                  <div className="room-quote-table-wrapper">
                    <table className="room-quote-table">
                      <thead>
                        <tr>
                          <th className="col-icon"></th>
                          <th className="col-name">Client Facing Name</th>
                          <th className="col-desc">Description</th>
                          <th className="col-color">Color</th>
                          <th className="col-qty">Qty</th>
                          <th className="col-price">Price</th>
                          <th className="col-ext">Extended Price</th>
                          <th className="col-check">
                            <div className="col-check-header">
                              <span className="col-check-label">Include</span>
                              <input
                                type="checkbox"
                                className="quote-checkbox"
                                checked={allChecked}
                                onChange={(e) => {
                                  const val = e.target.checked;
                                  setCheckedQuoteItems(prev => {
                                    const next = { ...prev };
                                    roomProducts.forEach(rp => { next[rp.id] = val; });
                                    return next;
                                  });
                                  setTaxableQuoteItems(prev => {
                                    const next = { ...prev };
                                    roomProducts.forEach(rp => { next[rp.id] = val; });
                                    return next;
                                  });
                                  // Persist all
                                  roomProducts.forEach(rp => {
                                    authFetch(`${CHAT_API_BASE}/room-products.php`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ id: rp.id, included: val ? 1 : 0, taxable: val ? 1 : 0 }),
                                    }).catch(() => {});
                                  });
                                }}
                              />
                            </div>
                          </th>
                          <th className="col-check">
                            <div className="col-check-header">
                              <span className="col-check-label">Taxable</span>
                              <input
                                type="checkbox"
                                className="quote-checkbox"
                                checked={allTaxable}
                                onChange={(e) => {
                                  const val = e.target.checked;
                                  setTaxableQuoteItems(prev => {
                                    const next = { ...prev };
                                    roomProducts.forEach(rp => { next[rp.id] = val; });
                                    return next;
                                  });
                                  roomProducts.forEach(rp => {
                                    authFetch(`${CHAT_API_BASE}/room-products.php`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ id: rp.id, taxable: val ? 1 : 0 }),
                                    }).catch(() => {});
                                  });
                                }}
                              />
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {roomProducts.map((rp) => {
                          const a = rp.attributes || {};
                          const qty = parseInt(a.quantity, 10) || 1;
                          const price = parseFloat(a.price) || 0;
                          const ext = price * qty;
                          const isChecked = !!checkedQuoteItems[rp.id];
                          const sourceImgs = productImages[a.source_product_id] || [];
                          const thumbUrl = sourceImgs.length > 0 && sourceImgs[0].image_path
                            ? ('/' + sourceImgs[0].image_path)
                            : null;
                          return (
                            <tr key={rp.id} className={isChecked ? '' : 'row-unchecked'}>
                              <td className="col-icon">
                                {thumbUrl ? (
                                  <div className="quote-product-icon">
                                    <img src={thumbUrl} alt="" />
                                  </div>
                                ) : (
                                  <div className="quote-product-icon placeholder">📦</div>
                                )}
                              </td>
                              <td className="col-name">{a.client_facing_name_c || a.name || '-'}</td>
                              <td className="col-desc">{a.description || '-'}</td>
                              <td className="col-color">{a.color_c || '-'}</td>
                              <td className="col-qty">{qty}</td>
                              <td className="col-price">{formatCurrency(price)}</td>
                              <td className="col-ext">{formatCurrency(ext)}</td>
                              <td className="col-check">
                                <input
                                  type="checkbox"
                                  className="quote-checkbox"
                                  checked={isChecked}
                                  onChange={(e) => {
                                    const val = e.target.checked;
                                    setCheckedQuoteItems(prev => ({ ...prev, [rp.id]: val }));
                                    setTaxableQuoteItems(prev => ({ ...prev, [rp.id]: val }));
                                    authFetch(`${CHAT_API_BASE}/room-products.php`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ id: rp.id, included: val ? 1 : 0, taxable: val ? 1 : 0 }),
                                    }).catch(() => {});
                                  }}
                                />
                              </td>
                              <td className="col-check">
                                <input
                                  type="checkbox"
                                  className="quote-checkbox"
                                  checked={taxableQuoteItems[rp.id] !== false}
                                  onChange={(e) => {
                                    const val = e.target.checked;
                                    setTaxableQuoteItems(prev => ({ ...prev, [rp.id]: val }));
                                    authFetch(`${CHAT_API_BASE}/room-products.php`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ id: rp.id, taxable: val ? 1 : 0 }),
                                    }).catch(() => {});
                                  }}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="quote-total-row">
                          <td colSpan="7" className="quote-total-label">Total</td>
                          <td className="col-ext quote-total-value">{formatCurrency(grandTotal)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                    <div className="quote-actions">
                      <button
                        className="btn-generate-quote"
                        onClick={handleGenerateQuote}
                        disabled={generatingQuote || !roomProducts.some(rp => checkedQuoteItems[rp.id])}
                      >
                        {generatingQuote ? 'Generating...' : 'Generate Proposal'}
                      </button>
                      <button
                        className="btn-create-invoice"
                        onClick={handleCreateInvoice}
                        disabled={creatingInvoice || !roomProducts.some(rp => checkedQuoteItems[rp.id])}
                      >
                        {creatingInvoice ? 'Creating...' : 'Create Invoice'}
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {activeTab === 'AOS_Invoices' && (
            <div className="room-module-section room-invoices-section">
              <h3 className="module-section-title">Invoices</h3>
              {selectedRoomInvoice ? (
                <div className="invoice-detail-card">
                  <InvoiceLedgerEdit
                    recordId={selectedRoomInvoice.id}
                    mode="invoice"
                    onCancel={() => { setSelectedRoomInvoice(null); setRoomInvoiceDetail(null); }}
                    onSaved={() => { setSelectedRoomInvoice(null); setRoomInvoiceDetail(null); fetchRoomInvoices(); }}
                  />
                </div>
              ) : loadingRoomInvoices ? (
                <div className="notes-loading">Loading invoices...</div>
              ) : roomInvoices.length === 0 ? (
                <div className="notes-empty">
                  <span className="empty-icon">🧾</span>
                  <p>No invoices yet. Create one from the Proposals tab.</p>
                </div>
              ) : (
                <div className="room-module-list">
                  {roomInvoices.map((inv) => {
                    const a = inv.attributes || {};
                    const formatCurrency = (v) => {
                      const n = parseFloat(v);
                      return isNaN(n) ? '' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
                    };
                    return (
                      <div
                        key={inv.id}
                        className="room-module-item invoice-card"
                        onClick={() => { setSelectedRoomInvoice(inv); fetchInvoiceDetail(inv.id); }}
                      >
                        <div className="room-module-item-name">{a.name || 'Untitled Invoice'}</div>
                        <div className="room-module-item-meta">
                          {a.number && <span className="room-module-item-status">#{a.number}</span>}
                          <span className={`status-badge status-${(a.status || '').toLowerCase().replace(/\s/g, '-')}`}>{a.status || 'Draft'}</span>
                          {a.total && <span className="room-product-price">{formatCurrency(a.total)}</span>}
                          {a.date_entered && (
                            <span className="room-module-item-date">{new Date(a.date_entered).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'Tasks' && (
            <>
              {editingRoomTask ? (() => {
                const ta = editingRoomTask.attributes || {};
                const RoomTaskEditInner = () => {
                  const [tf, setTf] = useState({
                    name: ta.name || '',
                    status: ta.status || 'Not Started',
                    priority: ta.priority || 'Medium',
                    assigned_user_id: ta.assigned_user_id || '',
                    date_start: ta.date_start || '',
                    date_due: ta.date_due || '',
                    description: ta.description || '',
                  });
                  const isEdit = !!editingRoomTask.id;
                  const tfRef = useRef(tf); tfRef.current = tf;
                  const saveCurrent = async () => {
                    const res = await authFetch(`${CHAT_API_BASE}/project-tasks.php`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ id: editingRoomTask.id, ...tfRef.current }),
                    });
                    const result = await res.json();
                    if (!result.success) throw new Error(result.error || 'Failed to save task');
                  };
                  const { schedule, flush, status: autoStatus } = useAutoSave(saveCurrent);
                  const set = (patch) => { setTf(p => ({ ...p, ...patch })); if (isEdit) schedule(); };
                  const handleDone = async () => { await flush(); handleSaveRoomTask(tfRef.current); };
                  return (
                    <div className="room-product-edit-page">
                      <div className="room-product-edit-header">
                        <button className="back-button" onClick={isEdit ? handleDone : () => setEditingRoomTask(null)}>← Back to Tasks</button>
                        <h2 className="room-product-edit-title">{!editingRoomTask.id ? (tf.name || 'New Task') : (tf.name || 'Edit Task')}</h2>
                        {ta.created_by_name && ta.created_by_name.trim() && (
                          <span className="task-created-by">Created by: {ta.created_by_name}</span>
                        )}
                        <div className="room-product-edit-actions">
                          {isEdit && <span className={`form-autosave form-autosave-${autoStatus}`}>{autoSaveLabel(autoStatus)}</span>}
                          <button className="btn-save-room-product" disabled={savingRoomTask}
                            onClick={isEdit ? handleDone : () => handleSaveRoomTask(tf)}>
                            {savingRoomTask ? 'Saving...' : (editingRoomTask.id ? 'Done' : 'Create Task')}
                          </button>
                        </div>
                      </div>
                      <div className="room-product-edit-grid">
                        <div className="room-product-edit-field">
                          <label>Subject</label>
                          <input type="text" value={tf.name} onChange={e => set({ name: e.target.value })} />
                        </div>
                        <div className="room-product-edit-field">
                          <label>Assigned To</label>
                          <select value={tf.assigned_user_id} onChange={e => set({ assigned_user_id: e.target.value })}>
                            <option value="">-- Select --</option>
                            {roomTaskUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                          </select>
                        </div>
                        <div className="room-product-edit-field">
                          <label>Status</label>
                          <select value={tf.status} onChange={e => set({ status: e.target.value })}>
                            {['Not Started','In Progress','Completed','Pending Input','Deferred'].map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <div className="room-product-edit-field">
                          <label>Priority</label>
                          <select value={tf.priority} onChange={e => set({ priority: e.target.value })}>
                            {['High','Medium','Low'].map(p => <option key={p} value={p}>{p}</option>)}
                          </select>
                        </div>
                        <div className="room-product-edit-field">
                          <label>Start Date</label>
                          <input type="datetime-local" value={(tf.date_start || '').replace(' ', 'T').substring(0,16)}
                            onChange={e => set({ date_start: e.target.value.replace('T',' ') + ':00' })} />
                        </div>
                        <div className="room-product-edit-field">
                          <label>Due Date</label>
                          <input type="datetime-local" value={(tf.date_due || '').replace(' ', 'T').substring(0,16)}
                            onChange={e => set({ date_due: e.target.value.replace('T',' ') + ':00' })} />
                        </div>
                        <div className="room-product-edit-field full-width">
                          <label>Description</label>
                          <textarea value={tf.description} rows={4} onChange={e => set({ description: e.target.value })} />
                        </div>
                      </div>
                      {editingRoomTask.id && (
                        <div className="room-notes-section" style={{ marginTop: '2rem' }}>
                          <h3 className="module-section-title">Notes</h3>
                          <PersistentTabNotes
                            parentType="task"
                            parentId={editingRoomTask.id}
                            defaultTitle="Notes"
                            mentionContext={{
                              projectId: project?.id || room.attributes?.project_id,
                              projectName: project?.attributes?.name || '',
                              roomId: room.id,
                              roomName: room.attributes?.name || 'Room',
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                };
                return <RoomTaskEditInner />;
              })() : (
                <>
                  <div className="project-tasks-toolbar">
                    <button className="btn-new-project-task" onClick={handleNewRoomTask}>
                      <span>➕</span> New Task
                    </button>
                  </div>
                  {loadingRoomTasks ? (
                    <div className="rooms-loading"><div className="spinner"></div><p>Loading tasks...</p></div>
                  ) : roomTasks.length === 0 ? (
                    <div className="notes-empty">
                      <span className="empty-icon"><img src={TasksIcon} alt="" style={{width:40,height:40,opacity:0.5}} /></span>
                      <p>No tasks for this room yet. Click <strong>+ New Task</strong> above to add one.</p>
                    </div>
                  ) : (
                    // Room task list — same grouped-row look as the standalone
                    // Tasks page so both views read as one design.
                    <div className="tp-embed">
                      <TaskListColumns />
                      {groupTasksBy(
                        roomTasks.map(t => ({ id: t.id, ...(t.attributes || {}) })),
                        'status',
                        {}
                      ).map(g => (
                        <TaskGroup
                          key={g.name}
                          group={g}
                          projectsByTaskId={{}}
                          subtaskCounts={roomSubtaskCounts}
                          onOpen={(id) => {
                            const t = roomTasks.find(x => x.id === id);
                            if (t) setEditingRoomTask(t);
                          }}
                          onToggleComplete={() => { /* room-tasks use their own edit flow */ }}
                          onAddTaskInGroup={handleNewRoomTask}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {MODULE_TABS.some(t => t.id === activeTab) && activeTab !== 'AOS_Products' && activeTab !== 'AOS_Quotes' && activeTab !== 'AOS_Invoices' && activeTab !== 'Tasks' && (
            <div className="room-module-section">
              <h3 className="module-section-title">
                {MODULE_TABS.find(t => t.id === activeTab)?.label || 'Records'}
              </h3>
              {loadingModuleRecords ? (
                <div className="notes-loading">Loading...</div>
              ) : moduleRecords.length === 0 ? (
                <div className="notes-empty">
                  <span className="empty-icon">{MODULE_TABS.find(t => t.id === activeTab)?.icon}</span>
                  <p>No {MODULE_TABS.find(t => t.id === activeTab)?.label?.toLowerCase()} found.</p>
                </div>
              ) : (
                <div className="room-module-list">
                  {moduleRecords.map((record) => {
                    const a = record.attributes || record;
                    const name = a.name || a.subject || [a.first_name, a.last_name].filter(Boolean).join(' ') || 'Untitled';
                    const status = a.status || a.stage || a.quote_stage || a.invoice_status || '';
                    const date = a.date_modified || a.date_entered || '';
                    const id = record.id || a.id;
                    return (
                      <div key={id} className="room-module-item">
                        <div className="room-module-item-name">{name}</div>
                        <div className="room-module-item-meta">
                          {status && <span className="room-module-item-status">{status}</span>}
                          {date && (
                            <span className="room-module-item-date">
                              {new Date(date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'drawings' && (
            <div className="room-drawings-section">
              <div className="media-add-form">
                <h4 className="media-form-title">Upload Drawing</h4>
                <div className="media-form-row">
                  <input
                    type="file"
                    id="drawing-file-input"
                    accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                    onChange={handleDrawingFileSelect}
                    className="media-file-input"
                  />
                  <input
                    type="text"
                    value={newDrawingName}
                    onChange={(e) => setNewDrawingName(e.target.value)}
                    placeholder="Drawing name (optional)"
                    className="media-input"
                  />
                </div>
                <div className="media-form-row">
                  <select
                    value={newDrawingType}
                    onChange={(e) => setNewDrawingType(e.target.value)}
                    className="media-select"
                  >
                    <option value="">-- Type --</option>
                    <option value="Floor Plan">Floor Plan</option>
                    <option value="Elevation">Elevation</option>
                    <option value="Section">Section</option>
                    <option value="Detail">Detail</option>
                    <option value="Electrical">Electrical</option>
                    <option value="Plumbing">Plumbing</option>
                    <option value="Other">Other</option>
                  </select>
                  <button
                    className="btn-add-media"
                    onClick={handleDrawingFileUpload}
                    disabled={uploadingDrawing || !selectedDrawingFile}
                  >
                    {uploadingDrawing ? 'Uploading...' : 'Upload'}
                  </button>
                </div>
                {selectedDrawingFile && (
                  <div className="media-preview">
                    {selectedDrawingFile.type === 'application/pdf' ? (
                      <div className="pdf-preview">PDF: {selectedDrawingFile.name}</div>
                    ) : (
                      <img src={URL.createObjectURL(selectedDrawingFile)} alt="Preview" />
                    )}
                  </div>
                )}

                <div className="media-form-divider">
                  <span>or add by URL</span>
                </div>

                <div className="media-form-row">
                  <input
                    type="text"
                    value={newDrawingUrl}
                    onChange={(e) => setNewDrawingUrl(e.target.value)}
                    placeholder="Drawing URL"
                    className="media-input media-url-input"
                  />
                  <button
                    className="btn-add-media"
                    onClick={handleAddDrawing}
                    disabled={savingDrawing || !newDrawingUrl.trim()}
                  >
                    {savingDrawing ? 'Adding...' : 'Add URL'}
                  </button>
                </div>
                {newDrawingUrl && (
                  <div className="media-preview">
                    <img src={newDrawingUrl} alt="Preview" onError={(e) => e.target.style.display = 'none'} />
                  </div>
                )}
              </div>

              {loadingDrawings ? (
                <div className="media-loading">Loading drawings...</div>
              ) : drawings.length === 0 ? (
                <div className="media-empty">
                  <span className="empty-icon">📐</span>
                  <p>No drawings yet. Add your first drawing above.</p>
                </div>
              ) : (
                <div className="media-grid">
                  {drawings.map(drawing => (
                    <div key={drawing.id} className="media-card">
                      <div className="media-card-image">
                        <img
                          src={drawing.attributes?.drawing_url}
                          alt={drawing.attributes?.name || 'Room drawing'}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '';
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                      <div className="media-card-body">
                        <h4 className="media-name">{drawing.attributes?.name || 'Untitled'}</h4>
                        {drawing.attributes?.drawing_type && (
                          <span className="media-type-badge">{drawing.attributes.drawing_type}</span>
                        )}
                        <span className="media-date">
                          {drawing.attributes?.date_entered
                            ? new Date(drawing.attributes.date_entered).toLocaleDateString()
                            : ''}
                        </span>
                        <button
                          className="btn-media-delete"
                          onClick={() => handleDeleteDrawing(drawing.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'layout' && (
            <div className="room-layout-section">
              <h3 className="module-section-title">Layout</h3>
              <RoomLayoutEditor roomId={room.id} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Project Edit Form Component
const ProjectEditForm = ({ project, onSave, onCancel }) => {
  const attrs = project?.attributes || {};
  const isEdit = !!project?.id;
  const [formData, setFormData] = useState({
    name: attrs.name || '',
    description: attrs.description || '',
    status: attrs.status || 'Draft',
    priority: attrs.priority || '',
    estimated_start_date: attrs.estimated_start_date || '',
    estimated_end_date: attrs.estimated_end_date || '',
    contact_id: attrs.contact_id || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Contact dropdown state
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [selectedContactName, setSelectedContactName] = useState(attrs.contact_name || '');

  // Contact detail modal state
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactDetails, setContactDetails] = useState(null);
  const [loadingContactDetails, setLoadingContactDetails] = useState(false);

  // Fetch contacts with search
  const fetchContacts = useCallback(async (search = '') => {
    setLoadingContacts(true);
    try {
      const url = search
        ? `${CHAT_API_BASE}/project-contacts.php?search=${encodeURIComponent(search)}`
        : `${CHAT_API_BASE}/project-contacts.php`;
      const response = await authFetch(url);
      const data = await response.json();
      if (data.success) {
        setContacts(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    }
    setLoadingContacts(false);
  }, []);

  // Fetch contact details
  const fetchContactDetails = async (contactId) => {
    setLoadingContactDetails(true);
    try {
      const response = await authFetch(`${CHAT_API_BASE}/project-contacts.php?id=${contactId}`);
      const data = await response.json();
      if (data.success) {
        setContactDetails(data.data);
        setShowContactModal(true);
      }
    } catch (err) {
      console.error('Failed to fetch contact details:', err);
    }
    setLoadingContactDetails(false);
  };

  // Load contacts when dropdown opens
  useEffect(() => {
    if (showContactDropdown) {
      fetchContacts(contactSearch);
    }
  }, [showContactDropdown, contactSearch, fetchContacts]);

  const handleContactSelect = (contact) => {
    setFormData(prev => ({ ...prev, contact_id: contact.id }));
    setSelectedContactName(contact.attributes?.full_name || '');
    setShowContactDropdown(false);
    setContactSearch('');
    if (isEdit) schedule();
  };

  const handleClearContact = () => {
    setFormData(prev => ({ ...prev, contact_id: '' }));
    setSelectedContactName('');
    if (isEdit) schedule();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (isEdit) schedule();
  };

  // Auto-save (edit mode only): persist current form via PUT.
  const saveCurrent = async () => {
    const response = await authFetch(`${CHAT_API_BASE}/projects.php`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: project?.id, user_id: currentUserId(), ...formData })
    });
    const data = await response.json();
    if (!response.ok || !data.success) throw new Error(data.error || 'Failed to save project');
  };
  const { schedule, flush, status: autoStatus } = useAutoSave(saveCurrent);

  const handleDone = async () => { await flush(); onSave(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEdit) { handleDone(); return; }
    setSaving(true);
    setError(null);

    try {
      const response = await authFetch(`${CHAT_API_BASE}/projects.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: project?.id,
          user_id: currentUserId(),
          ...formData
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save project');
      }

      const data = await response.json();
      if (data.success) {
        onSave(data.data);
      } else {
        throw new Error(data.error || 'Failed to save project');
      }
    } catch (err) {
      console.error('Failed to save project:', err);
      setError(err.message);
    }
    setSaving(false);
  };

  return (
    <div className="project-edit-page">
      <div className="project-edit-header">
        <button className="back-button" onClick={isEdit ? handleDone : onCancel}>
          ← Back to Projects
        </button>
        <h1 className="project-edit-title">
          {project?.id ? 'Edit Project' : 'New Project'}
        </h1>
      </div>

      <form className="project-edit-form" onSubmit={handleSubmit}>
        {error && (
          <div className="form-error">
            <span>⚠️</span> {error}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="name">Project Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Enter project name"
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            placeholder="Enter project description"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
            >
              {STATUS_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="priority">Priority</label>
            <select
              id="priority"
              name="priority"
              value={formData.priority}
              onChange={handleChange}
            >
              <option value="">-- Select --</option>
              {PRIORITY_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="estimated_start_date">Start Date</label>
            <input
              type="date"
              id="estimated_start_date"
              name="estimated_start_date"
              value={formData.estimated_start_date}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label htmlFor="estimated_end_date">End Date</label>
            <input
              type="date"
              id="estimated_end_date"
              name="estimated_end_date"
              value={formData.estimated_end_date}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Contact dropdown */}
        <div className="form-group contact-field">
          <label htmlFor="contact">Contact</label>
          <div className="contact-dropdown-container">
            <div className="contact-input-wrapper">
              <input
                type="text"
                id="contact"
                className="contact-input"
                placeholder="Search contacts..."
                value={showContactDropdown ? contactSearch : selectedContactName}
                onChange={(e) => {
                  setContactSearch(e.target.value);
                  if (!showContactDropdown) setShowContactDropdown(true);
                }}
                onFocus={() => setShowContactDropdown(true)}
              />
              {selectedContactName && !showContactDropdown && (
                <button
                  type="button"
                  className="contact-clear-btn"
                  onClick={handleClearContact}
                  title="Clear contact"
                >
                  ×
                </button>
              )}
            </div>

            {showContactDropdown && (
              <div className="contact-dropdown">
                <div className="contact-dropdown-header">
                  <span>Select a contact</span>
                  <button
                    type="button"
                    className="dropdown-close-btn"
                    onClick={() => {
                      setShowContactDropdown(false);
                      setContactSearch('');
                    }}
                  >
                    ×
                  </button>
                </div>
                <div className="contact-dropdown-list">
                  {loadingContacts ? (
                    <div className="contact-dropdown-loading">Loading contacts...</div>
                  ) : contacts.length === 0 ? (
                    <div className="contact-dropdown-empty">No contacts found</div>
                  ) : (
                    contacts.map(contact => (
                      <div
                        key={contact.id}
                        className="contact-dropdown-item"
                        onClick={() => handleContactSelect(contact)}
                      >
                        <span className="contact-name">{contact.attributes?.full_name}</span>
                        {contact.attributes?.email && (
                          <span className="contact-email">{contact.attributes.email}</span>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* View contact details button */}
            {formData.contact_id && (
              <button
                type="button"
                className="btn-view-contact"
                onClick={() => fetchContactDetails(formData.contact_id)}
                disabled={loadingContactDetails}
              >
                {loadingContactDetails ? 'Loading...' : 'View Contact'}
              </button>
            )}
          </div>
        </div>

        <div className="form-actions">
          {isEdit ? (
            <>
              <span className={`form-autosave form-autosave-${autoStatus}`}>{autoSaveLabel(autoStatus)}</span>
              <button type="button" className="btn-save" onClick={handleDone}>Done</button>
            </>
          ) : (
            <>
              <button type="button" className="btn-cancel" onClick={onCancel} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="btn-save" disabled={saving}>
                {saving ? 'Saving...' : 'Save Project'}
              </button>
            </>
          )}
        </div>
      </form>

      {/* Contact Details Modal */}
      {showContactModal && contactDetails && (
        <div className="modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
            <div className="contact-modal-header">
              <h2>Contact Details</h2>
              <button
                className="modal-close-btn"
                onClick={() => setShowContactModal(false)}
              >
                ×
              </button>
            </div>
            <div className="contact-modal-body">
              <div className="contact-detail-row">
                <label>Name:</label>
                <span>{contactDetails.attributes?.full_name || '-'}</span>
              </div>
              <div className="contact-detail-row">
                <label>Email:</label>
                <span>{contactDetails.attributes?.email || '-'}</span>
              </div>
              <div className="contact-detail-row">
                <label>Phone:</label>
                <span>{contactDetails.attributes?.phone_mobile || contactDetails.attributes?.phone_work || '-'}</span>
              </div>
              <div className="contact-detail-row">
                <label>Title:</label>
                <span>{contactDetails.attributes?.title || '-'}</span>
              </div>
              <div className="contact-detail-row">
                <label>Account:</label>
                <span>{contactDetails.attributes?.account_name || '-'}</span>
              </div>
              <div className="contact-detail-row">
                <label>Address:</label>
                <span>
                  {[
                    contactDetails.attributes?.primary_address_street,
                    contactDetails.attributes?.primary_address_city,
                    contactDetails.attributes?.primary_address_state,
                    contactDetails.attributes?.primary_address_postalcode
                  ].filter(Boolean).join(', ') || '-'}
                </span>
              </div>
              {contactDetails.attributes?.description && (
                <div className="contact-detail-row contact-description">
                  <label>Description:</label>
                  <span>{contactDetails.attributes.description}</span>
                </div>
              )}
            </div>
            <div className="contact-modal-footer">
              <button
                className="btn-close-modal"
                onClick={() => setShowContactModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Project Detail View Component

const ProjectDetailView = ({ project, onBack, onHome, deniedTabs }) => {
  // Per-user project sub-tab restrictions (keys like 'quotes','invoices').
  const tabDenied = (key) => !!deniedTabs && deniedTabs.has && deniedTabs.has(key);
  const currentUser = getCurrentUser();
  const [activeTab, setActiveTab] = useState('rooms');
  const [rooms, setRooms] = useState([]);
  const [roomImages, setRoomImages] = useState({});
  const [openRoomMenuId, setOpenRoomMenuId] = useState(null);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [editingRoom, setEditingRoom] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  // Breadcrumb: Projects → <project> → <tab>. When a room is open the room view
  // owns the (deeper) breadcrumb, so skip here to avoid clobbering it.
  useEffect(() => {
    if (selectedRoom) return;
    const projName = project?.attributes?.name || 'Project';
    setBreadcrumb(['Projects', projName, PROJECT_TAB_LABELS[activeTab] || prettyLabel(activeTab)]);
  }, [project, activeTab, selectedRoom]);

  // If the active sub-tab is restricted for this user, fall back to Rooms.
  useEffect(() => {
    if (activeTab !== 'rooms' && tabDenied(activeTab)) setActiveTab('rooms');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, deniedTabs]);

  // Track time spent on this project: starts on enter, ends on leave, program
  // exit (tab close/refresh), or 15-minute inactivity. See services/timeTracker.
  useEffect(() => {
    if (project?.id) startProjectTimer(project.id, currentUser?.id || null);
    return () => stopProjectTimer('leave');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  // Invoices state
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [creatingProjectInvoice, setCreatingProjectInvoice] = useState(false);

  // Quotes state
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [creatingProjectQuote, setCreatingProjectQuote] = useState(false);

  // Purchase Orders state
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loadingPurchaseOrders, setLoadingPurchaseOrders] = useState(false);
  const [generatingPOs, setGeneratingPOs] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);


  // Project Tasks state
  const [projectTasks, setProjectTasks] = useState([]);
  const [loadingProjectTasks, setLoadingProjectTasks] = useState(false);
  const [editingProjectTask, setEditingProjectTask] = useState(null);
  const [projectCountsVersion, setProjectCountsVersion] = useState(0);
  const projectSubtaskCounts = useSubtaskCounts(projectTasks, projectCountsVersion);
  // Ancestor tasks when drilling into subtasks, so a subtask can navigate back
  // up to its parent (the breadcrumb pops one level; Close/Done exit to the list).
  const [taskStack, setTaskStack] = useState([]);
  const [savingProjectTask, setSavingProjectTask] = useState(false);
  const [deletingProjectTask, setDeletingProjectTask] = useState(null);
  const [taskUsers, setTaskUsers] = useState([]);

  const attrs = project.attributes || {};

  const fetchRoomImages = useCallback(async (roomId) => {
    try {
      const response = await authFetch(`${CHAT_API_BASE}/room-images.php?room_id=${roomId}`);
      const data = await response.json();
      if (data.success && data.data && data.data.length > 0) {
        // Use the image the user chose for the card (is_cover), else the newest.
        const cover = data.data.find(i => Number(i.attributes?.is_cover) === 1) || data.data[0];
        setRoomImages(prev => ({
          ...prev,
          [roomId]: cover.attributes?.image_url
        }));
      }
    } catch (err) {
      console.error('Failed to fetch room images:', err);
    }
  }, []);

  const fetchRooms = useCallback(async () => {
    setLoadingRooms(true);
    try {
      const response = await authFetch(`${CHAT_API_BASE}/rooms.php?project_id=${project.id}`);
      const data = await response.json();
      if (data.success) {
        const roomList = data.data || [];
        setRooms(roomList);
        // Fetch first image for each room
        roomList.forEach(room => {
          fetchRoomImages(room.id);
        });
      }
    } catch (err) {
      console.error('Failed to fetch rooms:', err);
    }
    setLoadingRooms(false);
  }, [project.id, fetchRoomImages]);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  // Fetch invoices for project
  const fetchInvoices = useCallback(async () => {
    setLoadingInvoices(true);
    try {
      const response = await authFetch(`${CHAT_API_BASE}/project-invoices.php?project_id=${project.id}`);
      const data = await response.json();
      if (data.success) {
        setInvoices(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    }
    setLoadingInvoices(false);
  }, [project.id]);

  // Create a new (empty) invoice for this project. When the project already has
  // invoices, copy the most recent one's header info (billing address + billing
  // account/contact) onto the new invoice so the user doesn't retype it.
  const handleCreateProjectInvoice = async () => {
    setCreatingProjectInvoice(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const due = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];
      const projectName = project?.attributes?.name || 'Project';
      const body = {
        name: `${projectName} Invoice`,
        project_id: project.id,
        status: 'Draft',
        invoice_date: today,
        due_date: due,
        tax_amount: 0,
        shipping_amount: 0,
      };

      // Copy header from the most recent existing invoice, if any. The list
      // payload omits the billing address, so fetch that invoice by id.
      if (invoices.length > 0) {
        const newest = [...invoices].sort((a, b) =>
          new Date(b.attributes?.date_entered || 0) - new Date(a.attributes?.date_entered || 0)
        )[0];
        try {
          const srcRes = await authFetch(`${CHAT_API_BASE}/project-invoices.php?id=${newest.id}`);
          const srcData = await srcRes.json();
          if (srcData.success && srcData.data?.attributes) {
            const a = srcData.data.attributes;
            const addr = a.billing_address || {};
            if (a.account_id) body.billing_account_id = a.account_id;
            if (a.contact_id) body.billing_contact_id = a.contact_id;
            if (addr.street) body.billing_address_street = addr.street;
            if (addr.city) body.billing_address_city = addr.city;
            if (addr.state) body.billing_address_state = addr.state;
            if (addr.postalcode) body.billing_address_postalcode = addr.postalcode;
            if (addr.country) body.billing_address_country = addr.country;
          }
        } catch (e) {
          console.error('Failed to copy header from existing invoice:', e);
        }
      }

      const response = await authFetch(`${CHAT_API_BASE}/project-invoices.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (data.success && data.data?.id) {
        await fetchInvoices();
        // Open the new invoice in the editor.
        setSelectedInvoice(data.data);
      } else {
        alert('Failed to create invoice: ' + (data.error || data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Create project invoice error:', err);
      alert('Failed to create invoice.');
    }
    setCreatingProjectInvoice(false);
  };

  // Create an empty proposal for this project. Mirrors handleCreateProjectInvoice:
  // copies billing account/contact from the most recent existing proposal (so the
  // operator doesn't re-pick them), expiration 30 days out, Draft stage.
  const handleCreateProjectQuote = async () => {
    setCreatingProjectQuote(true);
    try {
      const expiration = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];
      const projectName = project?.attributes?.name || 'Project';
      const body = {
        name: `${projectName} Proposal`,
        project_id: project.id,
        stage: 'Draft',
        expiration,
        tax_amount: 0,
        shipping_amount: 0,
      };

      if (quotes.length > 0) {
        const newest = [...quotes].sort((a, b) =>
          new Date(b.attributes?.date_entered || 0) - new Date(a.attributes?.date_entered || 0)
        )[0];
        try {
          const srcRes = await authFetch(`${CHAT_API_BASE}/project-quotes.php?id=${newest.id}`);
          const srcData = await srcRes.json();
          if (srcData.success && srcData.data?.attributes) {
            const a = srcData.data.attributes;
            if (a.account_id) body.billing_account_id = a.account_id;
            if (a.contact_id) body.billing_contact_id = a.contact_id;
          }
        } catch (e) {
          console.error('Failed to copy header from existing proposal:', e);
        }
      }

      const response = await authFetch(`${CHAT_API_BASE}/project-quotes.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (data.success && data.data?.id) {
        await fetchQuotes();
        setSelectedQuote(data.data);
      } else {
        alert('Failed to create proposal: ' + (data.error || data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Create project proposal error:', err);
      alert('Failed to create proposal.');
    }
    setCreatingProjectQuote(false);
  };

  // Fetch quotes for project
  const fetchQuotes = useCallback(async () => {
    setLoadingQuotes(true);
    try {
      const response = await authFetch(`${CHAT_API_BASE}/project-quotes.php?project_id=${project.id}`);
      const data = await response.json();
      if (data.success) {
        setQuotes(data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch quotes:', err);
    }
    setLoadingQuotes(false);
  }, [project.id]);

  const fetchPurchaseOrders = useCallback(async () => {
    setLoadingPurchaseOrders(true);
    try {
      const response = await authFetch(`${CHAT_API_BASE}/purchase-orders.php?project_id=${project.id}`);
      const data = await response.json();
      const records = data?.data || [];
      setPurchaseOrders(Array.isArray(records) ? records : []);
    } catch (err) {
      console.error('Failed to fetch purchase orders:', err);
    }
    setLoadingPurchaseOrders(false);
  }, [project.id]);

  const handleGeneratePOs = async () => {
    setGeneratingPOs(true);
    try {
      const response = await authFetch(`${CHAT_API_BASE}/generate-purchase-orders.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: project.id }),
      });
      const data = await response.json();
      if (data.success) {
        alert(data.message);
        fetchPurchaseOrders();
      } else {
        alert('Failed: ' + (data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Generate POs error:', err);
      alert('Failed to generate purchase orders: ' + err.message);
    }
    setGeneratingPOs(false);
  };

  useEffect(() => {
    if (activeTab === 'invoices') {
      fetchInvoices();
    } else if (activeTab === 'quotes') {
      fetchQuotes();
    } else if (activeTab === 'purchase_orders') {
      fetchPurchaseOrders();
    } else if (activeTab === 'tasks') {
      fetchProjectTasks();
    }
  }, [activeTab, fetchInvoices, fetchQuotes, fetchPurchaseOrders]);

  const fetchProjectTasks = useCallback(async () => {
    setLoadingProjectTasks(true);
    try {
      // Backend filters to assigned_user_id = caller for non-admins; admin
      // check runs server-side against users.is_admin, not this param.
      const uid = currentUser?.id ? `&user_id=${encodeURIComponent(currentUser.id)}` : '';
      const response = await authFetch(`${CHAT_API_BASE}/project-tasks.php?project_id=${project.id}${uid}`);
      const data = await response.json();
      if (data.success) setProjectTasks(data.data || []);
    } catch (err) {
      console.error('Failed to fetch project tasks:', err);
    }
    setLoadingProjectTasks(false);
  }, [project.id, currentUser?.id]);

  // Fetch users for task assignment dropdown
  useEffect(() => {
    (async () => {
      try {
        const data = await api.chatRequest('GET', `${CHAT_API_BASE}/users.php`);
        if (data.success && data.users) setTaskUsers(data.users);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const handleSaveProjectTask = async (taskData) => {
    setSavingProjectTask(true);
    try {
      const isNew = !editingProjectTask?.id;
      if (isNew) {
        if (!taskData.name || !taskData.name.trim()) {
          alert('Task subject is required.');
          setSavingProjectTask(false);
          return;
        }
        await authFetch(`${CHAT_API_BASE}/project-tasks.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ project_id: project.id, created_by: currentUser?.id, ...taskData }),
        });
      } else {
        await authFetch(`${CHAT_API_BASE}/project-tasks.php`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: editingProjectTask.id, ...taskData }),
        });
      }
      setEditingProjectTask(null);
      setTaskStack([]);
      fetchProjectTasks();
    } catch (err) {
      console.error('Failed to save task:', err);
    }
    setSavingProjectTask(false);
  };

  const handleNewProjectTask = () => {
    setEditingProjectTask({ attributes: {} });
  };

  // Drill into a subtask, remembering the current task so the breadcrumb can
  // return to it. Opening a subtask shows the same detail (it is a full task).
  const openProjectSubtask = (subtask) => {
    setTaskStack(s => [...s, editingProjectTask]);
    setEditingProjectTask(subtask);
  };
  // Breadcrumb: pop one level back to the parent task (or exit to the list).
  const backToParentTask = () => {
    if (taskStack.length) {
      const parent = taskStack[taskStack.length - 1];
      setTaskStack(taskStack.slice(0, -1));
      setEditingProjectTask(parent);
    } else {
      setEditingProjectTask(null);
      fetchProjectTasks();
    }
  };

  // Delete a project task (used by both the card list and the detail view).
  // Returns true on success. Responses on the task remain in the record.
  const doDeleteProjectTask = async (taskId) => {
    try {
      const res = await authFetch(`${CHAT_API_BASE}/project-tasks.php?id=${encodeURIComponent(taskId)}&user_id=${encodeURIComponent(currentUser?.id || '')}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) { alert(data.message || 'Failed to delete task'); return false; }
      return true;
    } catch (err) {
      alert('Failed to delete task: ' + err.message);
      return false;
    }
  };

  // Card Delete button: confirm, delete, refresh the list (stays on the list).
  const deleteProjectTaskFromCard = async (e, task) => {
    e.stopPropagation();
    if (!window.confirm('Delete this task? Responses on it will remain in the record.')) return;
    setDeletingProjectTask(task.id);
    if (await doDeleteProjectTask(task.id)) fetchProjectTasks();
    setDeletingProjectTask(null);
  };

  // Conversation-bubble Delete (creator only): delete, then return to the list.
  const deleteProjectTaskFromDetail = async (task) => {
    if (!window.confirm('Delete this task? Responses on it will remain in the record.')) return;
    if (await doDeleteProjectTask(task.id)) { setEditingProjectTask(null); fetchProjectTasks(); }
  };

  const handleNewRoom = () => {
    setEditingRoom({});
  };

  const handleEditRoom = (e, room) => {
    e.stopPropagation();
    setEditingRoom(room);
  };

  const handleRoomSaved = () => {
    setEditingRoom(null);
    fetchRooms();
  };

  const handleRoomClick = (room) => {
    setSelectedRoom(room);
  };

  const handleDeleteClick = (e, room) => {
    e.stopPropagation();
    setShowDeleteConfirm(room);
  };

  const handleDeleteConfirm = async () => {
    if (!showDeleteConfirm) return;

    setDeleting(showDeleteConfirm.id);
    try {
      const response = await authFetch(`${CHAT_API_BASE}/rooms.php?id=${showDeleteConfirm.id}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        throw new Error('Failed to delete room');
      }
      setShowDeleteConfirm(null);
      fetchRooms();
    } catch (err) {
      console.error('Failed to delete room:', err);
      alert('Failed to delete room. Please try again.');
    }
    setDeleting(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(null);
  };

  // Show room detail view
  if (selectedRoom) {
    return (
      <RoomDetailView
        room={selectedRoom}
        project={project}
        onBack={() => setSelectedRoom(null)}
        onHome={onHome}
        onNavigateToProjectTab={(tab) => { setSelectedRoom(null); setActiveTab(tab); }}
        onCoverChanged={(roomId) => fetchRoomImages(roomId)}
      />
    );
  }

  // Show room edit form
  if (editingRoom) {
    return (
      <RoomEditForm
        room={editingRoom}
        projectId={project.id}
        onSave={handleRoomSaved}
        onCancel={() => setEditingRoom(null)}
      />
    );
  }

  // Show invoice ledger (editable)
  if (selectedInvoice) {
    return (
      <div className="project-detail-page">
        <div className="project-detail-back-row">
          <button className="back-button" onClick={() => setSelectedInvoice(null)}>← Back to Invoices</button>
        </div>
        <InvoiceLedgerEdit
          recordId={selectedInvoice.id}
          mode="invoice"
          onCancel={() => setSelectedInvoice(null)}
          onSaved={() => { setSelectedInvoice(null); fetchInvoices(); }}
        />
      </div>
    );
  }

  // Show quote ledger (editable)
  if (selectedQuote) {
    return (
      <div className="project-detail-page">
        <div className="project-detail-back-row">
          <button className="back-button" onClick={() => setSelectedQuote(null)}>← Back to Proposals</button>
        </div>
        <InvoiceLedgerEdit
          recordId={selectedQuote.id}
          mode="quote"
          onCancel={() => setSelectedQuote(null)}
          onSaved={() => { setSelectedQuote(null); fetchQuotes(); }}
          onConverted={() => { setSelectedQuote(null); setActiveTab('invoices'); fetchInvoices(); }}
          onConvertedToPO={() => { setSelectedQuote(null); setActiveTab('purchase_orders'); fetchPurchaseOrders(); }}
        />
      </div>
    );
  }

  // Show purchase order ledger (editable)
  if (selectedPO) {
    return (
      <div className="project-detail-page">
        <div className="project-detail-back-row">
          <button className="back-button" onClick={() => setSelectedPO(null)}>← Back to Purchase Orders</button>
        </div>
        <InvoiceLedgerEdit
          recordId={selectedPO.id}
          mode="purchase_order"
          onCancel={() => setSelectedPO(null)}
          onSaved={() => { setSelectedPO(null); fetchPurchaseOrders(); }}
        />
      </div>
    );
  }

  // Editing an existing project-level task takes over the whole page with
  // the same split-view TaskDetailView the standalone Tasks page uses, so
  // both surfaces read as one formatting. New-task creation still renders
  // inside the Tasks tab below.
  if (editingProjectTask?.id) {
    const flatTasks = projectTasks.map(t => ({ id: t.id, ...(t.attributes || {}) }));
    const flatTask = flatTasks.find(t => t.id === editingProjectTask.id)
      || { id: editingProjectTask.id, ...(editingProjectTask.attributes || {}) };
    const patchProjectTask = async (id, changes) => {
      // Optimistically update the currently-editing task so the right
      // column selects don't snap back — matters for subtasks, which
      // aren't in projectTasks and so wouldn't be picked up by
      // fetchProjectTasks.
      setEditingProjectTask(prev => (prev?.id === id)
        ? { ...prev, attributes: { ...(prev.attributes || {}), ...changes } }
        : prev);
      try {
        await authFetch(`${CHAT_API_BASE}/project-tasks.php`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id, ...changes, user_id: currentUser?.id }),
        });
      } catch { /* keep optimistic */ }
      fetchProjectTasks();
      setProjectCountsVersion(v => v + 1);
    };
    // Every task/subtask in this project's Tasks tab belongs to this project,
    // so resolve the Project field to it for any task id.
    const projInfo = { id: project?.id, name: project?.attributes?.name || 'Project' };
    const projectsByTaskId = new Proxy({}, { get: () => projInfo });
    return (
      <TaskDetailView
        task={flatTask}
        allTasks={flatTasks}
        projectsByTaskId={projectsByTaskId}
        onBack={() => { setTaskStack([]); setEditingProjectTask(null); fetchProjectTasks(); }}
        onSelect={(id, taskObj) => {
          // Subtasks aren't in projectTasks (that list is top-level tasks
          // only), so fall back to the passed subtask object.
          const next = projectTasks.find(t => t.id === id) || taskObj;
          if (next) setEditingProjectTask(next);
        }}
        onPatch={patchProjectTask}
        onRefresh={fetchProjectTasks}
        currentUser={currentUser}
        backLabel="← Back to Tasks"
      />
    );
  }

  return (
    <div className="project-detail-page">
      <div className="project-detail-header">
        <HomeButton onClick={onHome} />
        <button className="back-button" onClick={onBack}>
          ← Back to Projects
        </button>
        <div className="project-detail-title-section">
          <span className="project-detail-icon">📋</span>
          <h1 className="project-detail-title">{attrs.name || 'Unnamed Project'}</h1>
        </div>
      </div>

      <div className="project-detail-content">
        <div className="project-detail-sidebar">
          <button
            className={`project-detail-tab ${activeTab === 'rooms' ? 'active' : ''}`}
            onClick={() => setActiveTab('rooms')}
          >
            <span className="tab-icon"><img src={ProjectsIcon} alt="" className="tab-icon-svg" /></span>
            <span className="tab-label">Rooms</span>
          </button>
          <button
            className={`project-detail-tab ${activeTab === 'mood_board' ? 'active' : ''}`}
            onClick={() => setActiveTab('mood_board')}
          >
            <span className="tab-icon"><img src={MoodBoardIcon} alt="" className="tab-icon-svg" /></span>
            <span className="tab-label">Mood Board</span>
          </button>
          {!tabDenied('products_services') && (
          <button
            className={`project-detail-tab ${activeTab === 'products_services' ? 'active' : ''}`}
            onClick={() => setActiveTab('products_services')}
          >
            <span className="tab-icon"><img src={ProductsIcon} alt="" className="tab-icon-svg" /></span>
            <span className="tab-label">Products and Services</span>
          </button>
          )}
          {!tabDenied('project_tracker') && (
          <button
            className={`project-detail-tab ${activeTab === 'project_tracker' ? 'active' : ''}`}
            onClick={() => setActiveTab('project_tracker')}
          >
            <span className="tab-icon"><img src={ProjectTrackerIcon} alt="" className="tab-icon-svg" /></span>
            <span className="tab-label">Project Tracker</span>
          </button>
          )}
          {!tabDenied('quotes') && (
          <button
            className={`project-detail-tab ${activeTab === 'quotes' ? 'active' : ''}`}
            onClick={() => setActiveTab('quotes')}
          >
            <span className="tab-icon"><img src={QuotesIcon} alt="" className="tab-icon-svg" /></span>
            <span className="tab-label">Proposals</span>
          </button>
          )}
          {!tabDenied('invoices') && (
          <button
            className={`project-detail-tab ${activeTab === 'invoices' ? 'active' : ''}`}
            onClick={() => setActiveTab('invoices')}
          >
            <span className="tab-icon"><img src={InvoicesIcon} alt="" className="tab-icon-svg" /></span>
            <span className="tab-label">Invoices</span>
          </button>
          )}
          {!tabDenied('purchase_orders') && (
          <button
            className={`project-detail-tab ${activeTab === 'purchase_orders' ? 'active' : ''}`}
            onClick={() => setActiveTab('purchase_orders')}
          >
            <span className="tab-icon"><img src={PurchaseOrderIcon} alt="" className="tab-icon-svg" /></span>
            <span className="tab-label">Purchase Orders</span>
          </button>
          )}
          {!tabDenied('tasks') && (
          <button
            className={`project-detail-tab ${activeTab === 'tasks' ? 'active' : ''}`}
            onClick={() => setActiveTab('tasks')}
          >
            <span className="tab-icon"><img src={TasksIcon} alt="" className="tab-icon-svg" /></span>
            <span className="tab-label">Tasks</span>
          </button>
          )}
          {!tabDenied('retainers_credits_srisd') && (
          <button
            className={`project-detail-tab ${activeTab === 'retainers_credits_srisd' ? 'active' : ''}`}
            onClick={() => setActiveTab('retainers_credits_srisd')}
          >
            <span className="tab-icon"><img src={RcsIcon} alt="" className="tab-icon-svg" /></span>
            <span className="tab-label tab-label-stacked">Retainers<br/>Credits<br/>SRISD</span>
          </button>
          )}
          {!tabDenied('notes') && (
          <button
            className={`project-detail-tab ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            <span className="tab-icon"><img src={NotesIcon} alt="" className="tab-icon-svg" /></span>
            <span className="tab-label">Notes</span>
          </button>
          )}
          {!tabDenied('attachments') && (
          <button
            className={`project-detail-tab ${activeTab === 'attachments' ? 'active' : ''}`}
            onClick={() => setActiveTab('attachments')}
          >
            <span className="tab-icon"><img src={AttachmentsIcon} alt="" className="tab-icon-svg" /></span>
            <span className="tab-label">Attachments</span>
          </button>
          )}
        </div>

        <div className="project-detail-main">
          {/* Mood Board Tab Content */}
          {activeTab === 'mood_board' && (
            <MoodBoardEditor projectId={project.id} />
          )}
          {/* Products and Services Tab Content */}
          {activeTab === 'products_services' && (
            <ProjectProductsServices projectId={project.id} />
          )}

          {/* Attachments Tab Content */}
          {activeTab === 'attachments' && (
            <ProjectAttachments projectId={project.id} />
          )}

          {/* Project Tracker Tab Content */}
          {activeTab === 'project_tracker' && (
            <ProjectTracker projectId={project.id} />
          )}

          {/* Rooms Tab Content */}
          {activeTab === 'rooms' && (
            <div className="rooms-section">
              <div className="rooms-section-head">
                <div>
                  <h1 className="rooms-section-title">Rooms &amp; Spaces</h1>
                  <p className="rooms-section-sub">Organize specifications, products, and financials by room.</p>
                </div>
                <button className="rooms-new-btn" onClick={handleNewRoom}>+ New Room</button>
              </div>

              {loadingRooms ? (
                <div className="rooms-loading">
                  <div className="spinner"></div>
                  <p>Loading rooms...</p>
                </div>
              ) : rooms.length === 0 ? (
                <div className="rooms-empty">
                  <span className="empty-icon">🏠</span>
                  <h3>No Rooms Yet</h3>
                  <p>Click "New Room" to add a room to this project.</p>
                </div>
              ) : (
                <div className="rooms-grid">
                  {rooms.map((room) => {
                    const roomAttrs = room.attributes || {};
                    const img = roomImages[room.id] || roomAttrs.image_path;
                    const money = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })
                      .format(parseFloat(v) || 0);
                    const itemCount = Math.round(parseFloat(roomAttrs.total_item_count) || 0);
                    return (
                      <div
                        key={room.id}
                        className="room-card"
                        onClick={() => handleRoomClick(room)}
                      >
                        <div className="room-card-image">
                          {img ? (
                            <img
                              src={img}
                              alt={roomAttrs.name}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                                e.target.parentElement.classList.add('no-image');
                              }}
                            />
                          ) : (
                            <div className="room-card-placeholder">
                              <span className="room-card-placeholder-icon">🛋️</span>
                              <span className="room-card-placeholder-name">{roomAttrs.name || 'Room'}</span>
                            </div>
                          )}
                          <div className="room-card-menu-wrap">
                            <button
                              className="room-card-menu-btn"
                              title="Actions"
                              onClick={(e) => { e.stopPropagation(); setOpenRoomMenuId(openRoomMenuId === room.id ? null : room.id); }}
                            >⋯</button>
                            {openRoomMenuId === room.id && (
                              <>
                                <div className="project-menu-backdrop" onClick={(e) => { e.stopPropagation(); setOpenRoomMenuId(null); }} />
                                <div className="project-menu" onClick={(e) => e.stopPropagation()}>
                                  <button onClick={(e) => { setOpenRoomMenuId(null); handleEditRoom(e, room); }}>Edit</button>
                                  <button className="danger" onClick={(e) => { setOpenRoomMenuId(null); handleDeleteClick(e, room); }}>Delete</button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="room-card-body">
                          <div className="room-card-title-row">
                            <h3 className="room-name">{roomAttrs.name || 'Unnamed Room'}</h3>
                            <span className="room-card-chevron">›</span>
                          </div>
                          <div className="room-card-metrics">
                            <div className="room-metric">
                              <span className="room-metric-label">Items</span>
                              <span className="room-metric-value">{itemCount} item{itemCount === 1 ? '' : 's'}</span>
                            </div>
                            <div className="room-metric">
                              <span className="room-metric-label">Retail</span>
                              <span className="room-metric-value">{money(roomAttrs.total_items_value)}</span>
                            </div>
                            <div className="room-metric">
                              <span className="room-metric-label">Markup</span>
                              <span className="room-metric-value">{money(roomAttrs.total_markup)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Invoices Tab Content */}
          {activeTab === 'invoices' && (
            <>
              <div className="project-tasks-toolbar">
                <button
                  className="btn-new-project-task"
                  onClick={handleCreateProjectInvoice}
                  disabled={creatingProjectInvoice}
                >
                  <span>➕</span> {creatingProjectInvoice ? 'Creating…' : 'Create New Invoice'}
                </button>
              </div>
              {loadingInvoices ? (
                <div className="rooms-loading">
                  <div className="spinner"></div>
                  <p>Loading invoices...</p>
                </div>
              ) : invoices.length === 0 ? (
                <div className="rooms-empty">
                  <span className="empty-icon">🧾</span>
                  <h3>No Invoices Yet</h3>
                  <p>Click <strong>+ Create New Invoice</strong> above to add the first invoice for this project.</p>
                </div>
              ) : (
                <div className="invoices-list">
                  {invoices.map((invoice) => {
                    const invAttrs = invoice.attributes || {};
                    const sp = invAttrs.stripe_payment;
                    let stripeBadge = null;
                    if (sp && sp.status) {
                      const methodLabel = sp.method === 'us_bank_account' ? 'ACH' : (sp.method === 'card' ? 'Card' : '');
                      if (sp.status === 'paid')       stripeBadge = { cls: 'stripe-paid',       text: methodLabel ? `Paid · ${methodLabel}` : 'Paid via Stripe' };
                      else if (sp.status === 'partially_refunded') stripeBadge = { cls: 'stripe-refunded',   text: 'Partially refunded' };
                      else if (sp.status === 'refunded')          stripeBadge = { cls: 'stripe-refunded',   text: 'Refunded' };
                      else if (sp.status === 'processing') stripeBadge = { cls: 'stripe-processing', text: methodLabel ? `Processing · ${methodLabel}` : 'Processing' };
                      else if (sp.status === 'pending')    stripeBadge = { cls: 'stripe-pending',    text: 'Link sent' };
                      else if (sp.status === 'failed')     stripeBadge = { cls: 'stripe-failed',     text: 'Payment failed' };
                    }
                    return (
                      <div
                        key={invoice.id}
                        className="invoice-card"
                        onClick={() => setSelectedInvoice(invoice)}
                      >
                        <div className="invoice-card-header">
                          <span className="invoice-number">#{invAttrs.number || '-'}</span>
                          <span className={`invoice-status status-${(invAttrs.status || 'draft').toLowerCase().replace(/\s+/g, '-')}`}>
                            {invAttrs.status || 'Draft'}
                          </span>
                          {stripeBadge && (
                            <span className={`invoice-stripe-badge ${stripeBadge.cls}`} title={sp.paid_at ? `Paid ${new Date(sp.paid_at).toLocaleString()}` : ''}>
                              {stripeBadge.text}
                            </span>
                          )}
                        </div>
                        <div className="invoice-card-body">
                          <h3 className="invoice-name">{invAttrs.name || 'Untitled Invoice'}</h3>
                          {invAttrs.account_name && (
                            <p className="invoice-account">
                              <span className="account-icon">🏢</span>
                              {invAttrs.account_name}
                            </p>
                          )}
                          <div className="invoice-details">
                            <span className="invoice-date">
                              <span className="date-label">Date:</span>
                              {invAttrs.invoice_date ? new Date(invAttrs.invoice_date).toLocaleDateString() : '-'}
                            </span>
                            {invAttrs.due_date && (
                              <span className="invoice-due">
                                <span className="date-label">Due:</span>
                                {new Date(invAttrs.due_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <div className="invoice-total">
                            <span className="total-label">Total:</span>
                            <span className="total-amount">
                              ${parseFloat(invAttrs.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                          <button
                            className="btn-delete-card"
                            title="Delete invoice"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Delete invoice #${invAttrs.number || ''}?`)) {
                                authFetch(`${CHAT_API_BASE}/project-invoices.php?id=${invoice.id}`, { method: 'DELETE' })
                                  .then(() => fetchInvoices())
                                  .catch(err => console.error('Delete invoice failed:', err));
                              }
                            }}
                          >Delete</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Quotes Tab Content */}
          {activeTab === 'quotes' && (
            <>
              <div className="project-tasks-toolbar">
                <button
                  className="btn-new-project-task"
                  onClick={handleCreateProjectQuote}
                  disabled={creatingProjectQuote}
                >
                  <span>➕</span> {creatingProjectQuote ? 'Creating…' : 'Create New Proposal'}
                </button>
              </div>
              {loadingQuotes ? (
                <div className="rooms-loading">
                  <div className="spinner"></div>
                  <p>Loading proposals...</p>
                </div>
              ) : quotes.length === 0 ? (
                <div className="rooms-empty">
                  <span className="empty-icon">📝</span>
                  <h3>No Proposals Yet</h3>
                  <p>Click <strong>+ Create New Proposal</strong> above to add the first proposal for this project.</p>
                </div>
              ) : (
                <div className="invoices-list">
                  {quotes.map((quote) => {
                    const quoteAttrs = quote.attributes || {};
                    const sd = quoteAttrs.stripe_deposit;
                    let stripeBadge = null;
                    if (sd && sd.status) {
                      const amt = sd.amount ? ` $${parseFloat(sd.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';
                      if (sd.status === 'paid')         stripeBadge = { cls: 'stripe-paid',       text: `Deposit paid${amt}` };
                      else if (sd.status === 'processing') stripeBadge = { cls: 'stripe-processing', text: 'Deposit processing' };
                      else if (sd.status === 'pending')    stripeBadge = { cls: 'stripe-pending',    text: 'Deposit link sent' };
                      else if (sd.status === 'failed')     stripeBadge = { cls: 'stripe-failed',     text: 'Deposit failed' };
                    }
                    return (
                      <div
                        key={quote.id}
                        className="invoice-card"
                        onClick={() => setSelectedQuote(quote)}
                      >
                        <div className="invoice-card-header">
                          <span className="invoice-number">#{quoteAttrs.number || '-'}</span>
                          <span className={`invoice-status status-${(quoteAttrs.stage || 'draft').toLowerCase().replace(/\s+/g, '-')}`}>
                            {quoteAttrs.stage || 'Draft'}
                          </span>
                          {stripeBadge && (
                            <span className={`invoice-stripe-badge ${stripeBadge.cls}`} title={sd.paid_at ? `Paid ${new Date(sd.paid_at).toLocaleString()}` : ''}>
                              {stripeBadge.text}
                            </span>
                          )}
                        </div>
                        <div className="invoice-card-body">
                          <h3 className="invoice-name">{quoteAttrs.name || 'Untitled Proposal'}</h3>
                          {quoteAttrs.account_name && (
                            <p className="invoice-account">
                              <span className="account-icon">🏢</span>
                              {quoteAttrs.account_name}
                            </p>
                          )}
                          <div className="invoice-details">
                            <span className="invoice-date">
                              <span className="date-label">Created:</span>
                              {quoteAttrs.date_entered ? new Date(quoteAttrs.date_entered).toLocaleDateString() : '-'}
                            </span>
                            {quoteAttrs.expiration && (
                              <span className="invoice-due">
                                <span className="date-label">Expires:</span>
                                {new Date(quoteAttrs.expiration).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <div className="invoice-total">
                            <span className="total-label">Total:</span>
                            <span className="total-amount">
                              ${parseFloat(quoteAttrs.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                          <button
                            className="btn-delete-card"
                            title="Delete quote"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`Delete quote #${quoteAttrs.number || ''}?`)) {
                                authFetch(`${CHAT_API_BASE}/project-quotes.php?id=${quote.id}`, { method: 'DELETE' })
                                  .then(() => fetchQuotes())
                                  .catch(err => console.error('Delete quote failed:', err));
                              }
                            }}
                          >Delete</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Purchase Orders Tab Content */}
          {activeTab === 'purchase_orders' && (
            <>
              {/* List (PO detail handled by top-level selectedPO redirect to ledger) */}
              <div className="quote-actions" style={{marginBottom: '1rem'}}>
                    <button
                      className="btn-generate-quote"
                      onClick={handleGeneratePOs}
                      disabled={generatingPOs}
                    >
                      {generatingPOs ? 'Generating...' : 'Generate Purchase Orders'}
                    </button>
                  </div>
                  {loadingPurchaseOrders ? (
                    <div className="rooms-loading"><div className="spinner"></div><p>Loading purchase orders...</p></div>
                  ) : purchaseOrders.length === 0 ? (
                    <div className="rooms-empty">
                      <span className="empty-icon">📋</span>
                      <h3>No Purchase Orders Yet</h3>
                      <p>Click "Generate Purchase Orders" to create POs from room products.</p>
                    </div>
                  ) : (
                    <div className="invoices-list">
                      {purchaseOrders.map((po) => {
                        const poAttrs = po.attributes || {};
                        const fmt = (v) => { const n = parseFloat(v); return isNaN(n) ? '' : '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };
                        return (
                          <div key={po.id} className="invoice-card" onClick={() => setSelectedPO(po)}>
                            <div className="invoice-card-header">
                              <span className="invoice-number">{poAttrs.po_number || '-'}</span>
                              <span className={`invoice-status status-${(poAttrs.status || 'draft').toLowerCase().replace(/\s+/g, '-')}`}>
                                {poAttrs.status || 'Draft'}
                              </span>
                            </div>
                            <div className="invoice-card-body">
                              <h3 className="invoice-name">{poAttrs.name || 'Untitled PO'}</h3>
                              {poAttrs.vendor && <p className="invoice-account"><span className="account-icon">🏭</span>{poAttrs.vendor}</p>}
                              <div className="invoice-details">
                                <span className="invoice-date">
                                  <span className="date-label">Date:</span>
                                  {poAttrs.order_date ? new Date(poAttrs.order_date).toLocaleDateString() : '-'}
                                </span>
                              </div>
                              <div className="invoice-total">
                                <span className="total-label">Total:</span>
                                <span className="total-amount">{fmt(poAttrs.total)}</span>
                              </div>
                              <button
                                className="btn-delete-card"
                                title="Delete purchase order"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Delete ${poAttrs.po_number || 'this PO'}?`)) {
                                    authFetch(`${CHAT_API_BASE}/purchase-orders.php?id=${po.id}`, { method: 'DELETE' })
                                      .then(() => fetchPurchaseOrders())
                                      .catch(err => console.error('Delete PO failed:', err));
                                  }
                                }}
                              >Delete</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
            </>
          )}

          {/* Tasks Tab Content */}
          {activeTab === 'tasks' && (
            <>
              {editingProjectTask ? (() => {
                const taskAttrs = editingProjectTask.attributes || {};
                const TaskEditInner = () => {
                  const [tf, setTf] = useState({
                    name: taskAttrs.name || '',
                    status: taskAttrs.status || 'Not Started',
                    priority: taskAttrs.priority || 'Medium',
                    assigned_user_id: taskAttrs.assigned_user_id || '',
                    date_start: taskAttrs.date_start || '',
                    date_due: taskAttrs.date_due || '',
                    description: taskAttrs.description || '',
                  });
                  const isEdit = !!editingProjectTask.id;
                  const tfRef = useRef(tf); tfRef.current = tf;
                  const saveCurrent = async () => {
                    const res = await authFetch(`${CHAT_API_BASE}/project-tasks.php`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ id: editingProjectTask.id, ...tfRef.current }),
                    });
                    const result = await res.json();
                    if (!result.success) throw new Error(result.error || 'Failed to save task');
                  };
                  const { schedule, flush, status: autoStatus } = useAutoSave(saveCurrent);
                  const set = (patch) => { setTf(p => ({ ...p, ...patch })); if (isEdit) schedule(); };
                  const handleDone = async () => { await flush(); handleSaveProjectTask(tfRef.current); };
                  const handleClose = async () => { await flush(); setTaskStack([]); setEditingProjectTask(null); fetchProjectTasks(); };
                  const parentTaskName = taskStack.length ? (taskStack[taskStack.length - 1].attributes?.name || 'parent task') : '';
                  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';
                  const fmtDateTime = (d) => d ? new Date(d).toLocaleString() : '';
                  const assignedName = (taskUsers.find(u => u.id === tf.assigned_user_id)?.name)
                    || (taskAttrs.assigned_user_name || '').trim() || 'Unassigned';

                  // Existing task is handled at the project-detail top level
                  // via TaskDetailView (see the early-return above). Only the
                  // new-task create form falls through here.
                  if (isEdit) return null;

                  // New task -> a simple create form (no id yet, so no Notes/Conversation).
                  return (
                    <div className="room-product-edit-page">
                      <div className="room-product-edit-header">
                        <button className="back-button" onClick={() => setEditingProjectTask(null)}>← Back to Tasks</button>
                        <h2 className="room-product-edit-title">{tf.name || 'New Task'}</h2>
                        <div className="room-product-edit-actions">
                          <button className="btn-save-room-product" disabled={savingProjectTask}
                            onClick={() => handleSaveProjectTask(tf)}>
                            {savingProjectTask ? 'Saving...' : 'Create Task'}
                          </button>
                        </div>
                      </div>
                      <div className="room-product-edit-grid">
                        <div className="room-product-edit-field">
                          <label>Subject</label>
                          <input type="text" value={tf.name} onChange={e => set({ name: e.target.value })} />
                        </div>
                        <div className="room-product-edit-field">
                          <label>Assigned To</label>
                          <select value={tf.assigned_user_id} onChange={e => set({ assigned_user_id: e.target.value })}>
                            <option value="">-- Select --</option>
                            {taskUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                          </select>
                        </div>
                        <div className="room-product-edit-field">
                          <label>Status</label>
                          <select value={tf.status} onChange={e => set({ status: e.target.value })}>
                            {['Not Started','In Progress','Completed','Pending Input','Deferred'].map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <div className="room-product-edit-field">
                          <label>Priority</label>
                          <select value={tf.priority} onChange={e => set({ priority: e.target.value })}>
                            {['High','Medium','Low'].map(p => <option key={p} value={p}>{p}</option>)}
                          </select>
                        </div>
                        <div className="room-product-edit-field">
                          <label>Start Date</label>
                          <input type="datetime-local" value={(tf.date_start || '').replace(' ', 'T').substring(0,16)}
                            onChange={e => set({ date_start: e.target.value.replace('T',' ') + ':00' })} />
                        </div>
                        <div className="room-product-edit-field">
                          <label>Due Date</label>
                          <input type="datetime-local" value={(tf.date_due || '').replace(' ', 'T').substring(0,16)}
                            onChange={e => set({ date_due: e.target.value.replace('T',' ') + ':00' })} />
                        </div>
                        <div className="room-product-edit-field full-width">
                          <label>Description</label>
                          <textarea value={tf.description} rows={4} onChange={e => set({ description: e.target.value })} />
                        </div>
                      </div>
                    </div>
                  );
                };
                return <TaskEditInner />;
              })() : (
                <>
                  <div className="project-tasks-toolbar">
                    <button className="btn-new-project-task" onClick={handleNewProjectTask}>
                      <span>➕</span> New Task
                    </button>
                  </div>
                  {loadingProjectTasks ? (
                    <div className="rooms-loading"><div className="spinner"></div><p>Loading tasks...</p></div>
                  ) : projectTasks.length === 0 ? (
                    <div className="rooms-empty">
                      <span className="empty-icon"><img src={TasksIcon} alt="" style={{width:40,height:40,opacity:0.5}} /></span>
                      <h3>No Tasks Yet</h3>
                      <p>Click <strong>+ New Task</strong> above to add the first task for this project.</p>
                    </div>
                  ) : (
                    // Project task list — reuses the shared TaskGroup renderer
                    // so this reads as the same design as the standalone Tasks
                    // page and the Room > Tasks tab.
                    <div className="tp-embed">
                      <TaskListColumns />
                      {groupTasksBy(
                        projectTasks.map(t => ({ id: t.id, ...(t.attributes || {}) })),
                        'status',
                        {}
                      ).map(g => (
                        <TaskGroup
                          key={g.name}
                          group={g}
                          projectsByTaskId={{}}
                          subtaskCounts={projectSubtaskCounts}
                          onOpen={(id) => {
                            const t = projectTasks.find(x => x.id === id);
                            if (t) setEditingProjectTask(t);
                          }}
                          onToggleComplete={() => { /* project tasks use their own edit flow */ }}
                          onAddTaskInGroup={handleNewProjectTask}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Retainers / Credits / SRISD Tab Content — one running-balance
              account per category: deposits (manual or Stripe) add to the
              balance, invoice/proposal payments draw it down. */}
          {activeTab === 'retainers_credits_srisd' && (
            <RetainerCreditsPanel projectId={project.id} />
          )}

          {/* Notes Tab Content */}
          {activeTab === 'notes' && (
            <div className="room-notes-section">
              <div className="tab-notes-container">
                <PersistentTabNotes
                  parentType="project"
                  parentId={project.id}
                  defaultTitle="Notes"
                  mentionContext={{
                    projectId: project.id,
                    projectName: attrs.name || '',
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="delete-modal-overlay" onClick={handleDeleteCancel}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header">
              <span className="delete-modal-icon">⚠️</span>
              <h3>Delete Room?</h3>
            </div>
            <div className="delete-modal-body">
              <p>Are you sure you want to delete this room?</p>
              <p className="delete-project-name">
                {showDeleteConfirm.attributes?.name || 'Unnamed Room'}
              </p>
              <p className="delete-warning">This will also delete all notes associated with this room.</p>
            </div>
            <div className="delete-modal-actions">
              <button
                className="delete-modal-cancel"
                onClick={handleDeleteCancel}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="delete-modal-confirm"
                onClick={handleDeleteConfirm}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Projects Page Component
const ProjectsPage = ({ onBack, currentUser, deniedTabs, initialProjectId, onInitialConsumed }) => {
  const openedInitialRef = useRef(null);
  const [activeTab, setActiveTab] = useState('projects');
  const [projects, setProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent'); // 'recent' | 'name'
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [openMenuId, setOpenMenuId] = useState(null);

  // Breadcrumb at the project-list level; the detail view takes over when a
  // project is open.
  useEffect(() => {
    if (!selectedProject) setBreadcrumb(['Projects']);
  }, [selectedProject]);

  const tabs = [
    { id: 'projects', label: 'Projects', icon: '📋' }
  ];

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const uid = currentUserId();
      const response = await authFetch(`${CHAT_API_BASE}/projects.php${uid ? `?user_id=${encodeURIComponent(uid)}` : ''}`);
      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }
      const data = await response.json();

      const projectList = data.data || [];
      setAllProjects(projectList);

      const uniqueStatuses = [...new Set(
        projectList
          .map(p => p.attributes?.status)
          .filter(s => s && s.trim())
      )].sort();
      setCategories(uniqueStatuses);

      if (selectedCategory) {
        const filtered = projectList.filter(p => p.attributes?.status === selectedCategory);
        setProjects(filtered);
      } else {
        setProjects(projectList);
      }

      if (data.meta && data.meta.total) {
        const total = Math.ceil(data.meta.total / 20);
        setTotalPages(total);
      }

      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      setError('Failed to load projects. Please try again.');
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchProjects();
  }, [currentPage, fetchProjects]);

  // Deep link from the dashboard: open a specific project once loaded. Fetches
  // it directly if it isn't in the current list page. Runs once per id.
  useEffect(() => {
    if (!initialProjectId || openedInitialRef.current === initialProjectId) return;
    openedInitialRef.current = initialProjectId;
    (async () => {
      try {
        let proj = allProjects.find(p => p.id === initialProjectId);
        if (!proj) {
          const uid = currentUserId();
          const res = await authFetch(`${CHAT_API_BASE}/projects.php?id=${encodeURIComponent(initialProjectId)}${uid ? `&user_id=${encodeURIComponent(uid)}` : ''}`);
          const data = await res.json();
          proj = Array.isArray(data.data) ? data.data[0] : data.data;
        }
        if (proj && proj.id) setSelectedProject(proj);
      } catch (e) {
        console.error('Failed to open project from dashboard:', e);
      }
      if (onInitialConsumed) onInitialConsumed();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialProjectId, allProjects]);

  useEffect(() => {
    if (allProjects.length > 0) {
      if (selectedCategory) {
        const filtered = allProjects.filter(p => p.attributes?.status === selectedCategory);
        setProjects(filtered);
      } else {
        setProjects(allProjects);
      }
    }
  }, [selectedCategory, allProjects]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  const fmtDateShort = (d) => {
    if (!d) return null;
    try { return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return d; }
  };
  const projectDateRange = (a) => {
    const s = fmtDateShort(a.estimated_start_date);
    const e = fmtDateShort(a.estimated_end_date);
    if (!s && !e) return null;
    return `${s || 'TBD'} – ${e || 'TBD'}`;
  };

  const handleDeleteClick = (e, project) => {
    e.stopPropagation();
    setShowDeleteConfirm(project);
  };

  const handleDeleteConfirm = async () => {
    if (!showDeleteConfirm) return;

    setDeleting(showDeleteConfirm.id);
    try {
      const uid = currentUserId();
      const response = await authFetch(`${CHAT_API_BASE}/projects.php?id=${showDeleteConfirm.id}${uid ? `&user_id=${encodeURIComponent(uid)}` : ''}`, {
        method: 'DELETE'
      });
      if (!response.ok) {
        throw new Error('Failed to delete project');
      }
      setShowDeleteConfirm(null);
      await fetchProjects();
    } catch (err) {
      console.error('Failed to delete project:', err);
      alert('Failed to delete project. Please try again.');
    }
    setDeleting(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(null);
  };

  const handleProjectClick = (project) => {
    setSelectedProject(project);
  };

  const handleEditClick = (e, project) => {
    e.stopPropagation();
    setEditingProject(project);
  };

  const handleEditSave = async () => {
    setEditingProject(null);
    await fetchProjects();
  };

  const handleEditCancel = () => {
    setEditingProject(null);
  };

  const handleNewProject = () => {
    setEditingProject({});
  };

  // Show project edit form if editing
  if (editingProject) {
    return (
      <ProjectEditForm
        project={editingProject}
        onSave={handleEditSave}
        onCancel={handleEditCancel}
      />
    );
  }

  // Show project detail view if a project is selected
  if (selectedProject) {
    return (
      <ProjectDetailView
        project={selectedProject}
        onBack={() => setSelectedProject(null)}
        onHome={onBack}
        currentUser={currentUser}
        deniedTabs={deniedTabs}
      />
    );
  }

  const displayed = [...projects]
    .filter((p) => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return true;
      const a = p.attributes || {};
      return (a.name || '').toLowerCase().includes(q) || (a.description || '').toLowerCase().includes(q);
    })
    .sort((p1, p2) => {
      const a = p1.attributes || {}, b = p2.attributes || {};
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      return new Date(b.date_modified || 0) - new Date(a.date_modified || 0);
    });

  return (
    <div className="projects-page projects-page-v2">
      <div className="projects-topbar">
        <button className="back-button" onClick={onBack}>← Dashboard</button>
        <div className="projects-topbar-right">
          <select
            className="projects-status-filter"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">All Projects</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <button className="projects-refresh" onClick={fetchProjects} disabled={loading}>
            {loading ? '⟳' : '↻'} Refresh
          </button>
        </div>
      </div>

      <div className="projects-hero">
        <div className="projects-hero-left">
          <HomeButton onClick={onBack} />
          <div className="projects-hero-text">
            <h1 className="projects-hero-title">Projects</h1>
            <p className="projects-hero-sub">Manage all of your interior design projects in one place.</p>
          </div>
        </div>
        <button className="projects-new-btn" onClick={handleNewProject}>+ New Project</button>
      </div>

      <div className="projects-toolbar">
        <div className="projects-search">
          <span className="projects-search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="projects-toolbar-right">
          <label className="projects-sort">
            <span>Sort by:</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="recent">Recent Updated</option>
              <option value="name">Name</option>
            </select>
          </label>
          <div className="projects-view-toggle">
            <button
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >▦</button>
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
              title="List view"
            >☰</button>
          </div>
        </div>
      </div>

      <div className="projects-body">
        {loading && (
          <div className="projects-loading">
            <div className="spinner"></div>
            <p>Loading projects...</p>
          </div>
        )}

        {error && (
          <div className="projects-error">
            <p>{error}</p>
            <button onClick={fetchProjects}>Try Again</button>
          </div>
        )}

        {!loading && !error && displayed.length === 0 && (
          <div className="projects-empty">
            <span className="empty-icon">📋</span>
            <h3>No Projects Found</h3>
            <p>{searchQuery || selectedCategory ? 'No projects match your filters.' : 'There are no projects in the system yet.'}</p>
          </div>
        )}

        {!loading && !error && displayed.length > 0 && (
          <>
            <div className={`projects-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
              {displayed.map((project) => {
                const attrs = project.attributes || {};
                const range = projectDateRange(attrs);
                const owner = (attrs.assigned_user_name || '').trim();
                return (
                  <div
                    key={project.id}
                    className="project-card"
                    onClick={() => handleProjectClick(project)}
                  >
                    <div className="project-card-top">
                      <span className={`project-status-badge status-${(attrs.status || 'draft').toLowerCase().replace(/\s+/g, '-')}`}>
                        {attrs.status || 'Draft'}
                      </span>
                      {attrs.priority && (
                        <span className={`project-priority-badge priority-${attrs.priority.toLowerCase()}`}>
                          {attrs.priority}
                        </span>
                      )}
                      <div className="project-menu-wrap">
                        <button
                          className="project-menu-btn"
                          title="Actions"
                          onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === project.id ? null : project.id); }}
                        >⋯</button>
                        {openMenuId === project.id && (
                          <>
                            <div className="project-menu-backdrop" onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); }} />
                            <div className="project-menu" onClick={(e) => e.stopPropagation()}>
                              <button onClick={(e) => { setOpenMenuId(null); handleEditClick(e, project); }}>Edit</button>
                              <button className="danger" onClick={(e) => { setOpenMenuId(null); handleDeleteClick(e, project); }}>Delete</button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <h3 className="project-name">{attrs.name || 'Unnamed Project'}</h3>
                    {attrs.description && <p className="project-description">{attrs.description}</p>}

                    {range && (
                      <div className="project-daterow">
                        <span className="project-cal-icon">📅</span> {range}
                      </div>
                    )}

                    <div className="project-owner">
                      <span className="project-owner-avatar">{(owner || '?').charAt(0).toUpperCase()}</span>
                      <div className="project-owner-meta">
                        <span className="project-owner-name">{owner || 'Unassigned'}</span>
                        <span className="project-owner-role">Project Owner</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="projects-count">
              <span className="projects-count-icon">🗂️</span>
              Showing {displayed.length} of {projects.length} project{projects.length === 1 ? '' : 's'}
            </div>
          </>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
            <div className="delete-modal-overlay" onClick={handleDeleteCancel}>
              <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
                <div className="delete-modal-header">
                  <span className="delete-modal-icon">⚠️</span>
                  <h3>Delete Project?</h3>
                </div>
                <div className="delete-modal-body">
                  <p>Are you sure you want to delete this project?</p>
                  <p className="delete-project-name">
                    {showDeleteConfirm.attributes?.name || `Project #${showDeleteConfirm.id.slice(0, 8)}`}
                  </p>
                  <p className="delete-warning">This action cannot be undone.</p>
                </div>
                <div className="delete-modal-actions">
                  <button
                    className="delete-modal-cancel"
                    onClick={handleDeleteCancel}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    className="delete-modal-confirm"
                    onClick={handleDeleteConfirm}
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
    </div>
  );
};

export default ProjectsPage;
